import React, { useRef, useEffect, useCallback, useState } from 'react';
import { createAudioPool, playSound } from '../audio/gameAudio';
import { GameStartSequence } from '../components';
import { useGameStartSequence } from '../hooks';
import { useFont } from '../../contexts/FontContext';
import { isProceduralBackground } from '../proceduralBackgrounds';
import { ensureMinResolution } from '../imageUtils';

const GOLF_SOUND_MAP = { collect: 'collectSound', death: 'deathSound', bounce: 'bounceSound' };

// ── Virtual canvas & physics constants ─────────────────────
export const VW = 400;
export const VH = 700;
const REF_VIRTUAL_WIDTH = 440;   // Wider than VW so sky is always visible around the island
const REF_VIRTUAL_HEIGHT = 800;  // Taller than VH for top/bottom breathing room
const BALL_R = 14;
const HOLE_R = 15;
const FRICTION = 0.985;
const MAX_POWER = 18;
const MAX_DRAG = 130;
const STOP_V = 0.3;
const HOLE_CAPTURE_SPEED = 3.5;
const BOUNCE_LOSS = 0.82;
const MAX_STROKES = 10;
const DEFAULT_HOLE_TIME = 30; // 30s timer per hole (visual only, never forces end)
const POINTS_BASE = 200;           // Max points per hole
const POINTS_STROKE_COST = 20;     // Deducted per stroke
const POINTS_TIME_PENALTY = 1;     // Deducted per second elapsed
const POINTS_MIN = 5;              // Minimum points when holed

// Play area — full canvas with small padding (no top HUD)
export const PA = { x: 20, y: 20, w: 360, h: 660 };
export const OBS_SIZE = 34; // Default obstacle square size

// ── 2.5D floating island constants ───────────────────────
const DEPTH = 28;            // Island thickness in virtual pixels
const SIDE_DARKEN = 0.35;    // Side wall darkness (bottom-facing)
const SIDE_DARKEN_LIGHT = 0.25; // Side wall darkness (right-facing, lighter for directional light)
const OBS_DEPTH = 17;        // Obstacle block depth (shorter than island)

// ── Hole pool by difficulty ────────────────────────────────
// Creative, gameplay-driven layouts. All course shapes use only
// right angles (rectilinear polygons) for clean joints.
// openEdges: array of edge indices where the ball can fly off.
// PA: x 20–380, y 20–680.
export const HOLE_POOL = {
  easy: [
    { name: "Wide Bend", par: 2, ball: { x: 80, y: 600 }, hole: { x: 320, y: 80 }, obstacles: [{ x: 250, y: 250, w: 34, h: 34 }], openEdges: [2], courseShape: [{"x":180,"y":20},{"x":380,"y":20},{"x":380,"y":420},{"x":180,"y":420},{"x":180,"y":680},{"x":20,"y":680},{"x":20,"y":500},{"x":120,"y":500},{"x":120,"y":160},{"x":180,"y":160}] },
    { name: "Peninsula", par: 2, ball: { x: 300, y: 600 }, hole: { x: 200, y: 186 }, obstacles: [], openEdges: [0, 1], courseShape: [{"x":140,"y":120},{"x":260,"y":120},{"x":260,"y":420},{"x":380,"y":420},{"x":380,"y":680},{"x":20,"y":680},{"x":20,"y":420},{"x":140,"y":420}] },
    { name: "Alcove", par: 2, ball: { x: 200, y: 580 }, hole: { x: 320, y: 140 }, obstacles: [{ x: 182, y: 152, w: 34, h: 34 }], openEdges: [], courseShape: [{"x":114,"y":20},{"x":250,"y":20},{"x":250,"y":80},{"x":380,"y":80},{"x":380,"y":200},{"x":250,"y":200},{"x":250,"y":260},{"x":300,"y":260},{"x":300,"y":680},{"x":114,"y":680}], chamfers: [{ vertex: 0, size: 40 }] },
    { name: "Bottleneck", par: 2, ball: { x: 100, y: 600 }, hole: { x: 300, y: 100 }, obstacles: [{ x: 182, y: 302, w: 34, h: 34 }, { x: 182, y: 360, w: 34, h: 34 }, { x: 182, y: 242, w: 34, h: 34 }], openEdges: [2, 5], courseShape: [{"x":20,"y":20},{"x":352,"y":20},{"x":352,"y":192},{"x":280,"y":192},{"x":280,"y":680},{"x":20,"y":680}], chamfers: [{ vertex: 0, size: 40 }] },
    { name: "Notch", par: 2, ball: { x: 300, y: 580 }, hole: { x: 100, y: 100 }, obstacles: [{ x: 250, y: 200, w: 34, h: 34 }, { x: 300, y: 450, w: 34, h: 34 }], openEdges: [], courseShape: [{"x":20,"y":20},{"x":380,"y":20},{"x":380,"y":680},{"x":180,"y":680},{"x":180,"y":340},{"x":20,"y":340}] },
    { name: "T-Green", par: 2, ball: { x: 200, y: 620 }, hole: { x: 70, y: 134 }, obstacles: [{ x: 168, y: 402, w: 34, h: 34 }], openEdges: [0, 2], courseShape: [{"x":20,"y":80},{"x":374,"y":80},{"x":374,"y":240},{"x":260,"y":240},{"x":260,"y":680},{"x":140,"y":680},{"x":140,"y":240},{"x":20,"y":240}] },
    { name: "Funnel", par: 2, ball: { x: 328, y: 632 }, hole: { x: 170, y: 60 }, obstacles: [{ x: 183, y: 210, w: 34, h: 34 }, { x: 100, y: 500, w: 34, h: 34 }], openEdges: [1, 5], courseShape: [{"x":140,"y":20},{"x":260,"y":20},{"x":260,"y":200},{"x":380,"y":200},{"x":380,"y":680},{"x":20,"y":680},{"x":20,"y":200},{"x":140,"y":200}] },
    { name: "Bridge", par: 2, ball: { x: 330, y: 552 }, hole: { x: 92, y: 72 }, obstacles: [{ x: 80, y: 180, w: 34, h: 34 }], openEdges: [3, 9], courseShape: [{"x":40,"y":20},{"x":314,"y":20},{"x":314,"y":280},{"x":240,"y":280},{"x":240,"y":520},{"x":360,"y":520},{"x":360,"y":680},{"x":100,"y":680},{"x":100,"y":420},{"x":160,"y":420},{"x":160,"y":280},{"x":40,"y":280}], chamfers: [{ vertex: 7, size: 40 }, { vertex: 6, size: 40 }] },
    { name: "Ramp", par: 2, ball: { x: 80, y: 620 }, hole: { x: 316, y: 74 }, obstacles: [{ x: 200, y: 360, w: 34, h: 34 }], openEdges: [2], courseShape: [{"x":200,"y":20},{"x":364,"y":20},{"x":364,"y":280},{"x":280,"y":280},{"x":280,"y":440},{"x":324,"y":440},{"x":324,"y":680},{"x":20,"y":680},{"x":20,"y":440},{"x":100,"y":440},{"x":100,"y":200},{"x":200,"y":200}] },
    { name: "Bay", par: 2, ball: { x: 78, y: 634 }, hole: { x: 322, y: 628 }, obstacles: [{ x: 182, y: 32, w: 34, h: 34 }], openEdges: [0], courseShape: [{"x":20,"y":20},{"x":380,"y":20},{"x":380,"y":680},{"x":260,"y":680},{"x":260,"y":126},{"x":140,"y":126},{"x":140,"y":680},{"x":20,"y":680}] },
    { name: "The Plank", par: 2, ball: { x: 200, y: 600 }, hole: { x: 200, y: 100 }, obstacles: [{ x: 183, y: 350, w: 34, h: 34 }], openEdges: [1, 3, 5, 7], courseShape: [{"x":130,"y":40},{"x":270,"y":40},{"x":270,"y":310},{"x":270,"y":400},{"x":270,"y":660},{"x":130,"y":660},{"x":130,"y":400},{"x":130,"y":310}] },
    { name: "The Precipice", par: 2, ball: { x: 100, y: 600 }, hole: { x: 320, y: 80 }, obstacles: [{ x: 180, y: 320, w: 34, h: 34 }, { x: 290, y: 170, w: 34, h: 34 }], openEdges: [0, 1], courseShape: [{"x":20,"y":20},{"x":380,"y":20},{"x":380,"y":280},{"x":260,"y":280},{"x":260,"y":680},{"x":20,"y":680}] },
    { name: "The Slope", par: 2, ball: { x: 100, y: 604 }, hole: { x: 294, y: 74 }, obstacles: [{ x: 200, y: 320, w: 34, h: 34 }, { x: 120, y: 160, w: 34, h: 34 }], openEdges: [1], courseShape: [{"x":52,"y":20},{"x":354,"y":20},{"x":354,"y":680},{"x":52,"y":680}], chamfers: [{ vertex: 0, size: 40 }] },
    { name: "The Nook", par: 2, ball: { x: 280, y: 580 }, hole: { x: 80, y: 80 }, obstacles: [{ x: 120, y: 338, w: 34, h: 34 }], openEdges: [], courseShape: [{"x":20,"y":20},{"x":160,"y":20},{"x":160,"y":302},{"x":380,"y":302},{"x":380,"y":680},{"x":20,"y":680}], chamfers: [{ vertex: 5, size: 40 }] },
    { name: "The Elbow", par: 2, ball: { x: 320, y: 446 }, hole: { x: 100, y: 100 }, obstacles: [{ x: 150, y: 320, w: 34, h: 34 }], openEdges: [0], courseShape: [{"x":20,"y":20},{"x":240,"y":20},{"x":240,"y":400},{"x":380,"y":400},{"x":380,"y":680},{"x":20,"y":680}], chamfers: [{ vertex: 5, size: 40 }] },
    { name: "The Drop", par: 2, ball: { x: 200, y: 580 }, hole: { x: 200, y: 80 }, obstacles: [{ x: 183, y: 220, w: 34, h: 34 }], openEdges: [1, 5], courseShape: [{"x":120,"y":20},{"x":280,"y":20},{"x":280,"y":280},{"x":380,"y":280},{"x":380,"y":680},{"x":20,"y":680},{"x":20,"y":280},{"x":120,"y":280}], chamfers: [{ vertex: 0, size: 40 }] },
    { name: "The Step", par: 2, ball: { x: 120, y: 600 }, hole: { x: 300, y: 200 }, obstacles: [{ x: 230, y: 300, w: 34, h: 34 }], openEdges: [1], courseShape: [{"x":140,"y":20},{"x":380,"y":20},{"x":380,"y":380},{"x":260,"y":380},{"x":260,"y":680},{"x":20,"y":680},{"x":20,"y":380},{"x":140,"y":380}], chamfers: [{ vertex: 0, size: 40 }, { vertex: 4, size: 40 }] },
    { name: "The Cushion", par: 2, ball: { x: 100, y: 600 }, hole: { x: 320, y: 100 }, obstacles: [{ x: 260, y: 240, w: 34, h: 34 }], openEdges: [2], courseShape: [{"x":20,"y":20},{"x":380,"y":20},{"x":380,"y":340},{"x":200,"y":340},{"x":200,"y":680},{"x":20,"y":680}], chamfers: [{ vertex: 0, size: 40 }] },
    { name: "Cross Court", par: 2, ball: { x: 80, y: 580 }, hole: { x: 320, y: 100 }, obstacles: [{ x: 198, y: 290, w: 34, h: 34 }, { x: 254, y: 290, w: 34, h: 34 }], openEdges: [], courseShape: [{"x":40,"y":40},{"x":360,"y":40},{"x":360,"y":660},{"x":40,"y":660}] },
  ],
  medium: [
    { name: "Dog Leg", par: 3, ball: { x: 80, y: 560 }, hole: { x: 320, y: 80 }, obstacles: [], openEdges: [2], courseShape: [{"x":240,"y":20},{"x":380,"y":20},{"x":380,"y":294},{"x":280,"y":294},{"x":280,"y":212},{"x":150,"y":212},{"x":150,"y":680},{"x":20,"y":680},{"x":20,"y":480},{"x":86,"y":480},{"x":86,"y":140},{"x":240,"y":140}], chamfers: [{ vertex: 0, size: 40 }, { vertex: 6, size: 40 }, { vertex: 10, size: 40 }] },
    { name: "Split Path", par: 3, ball: { x: 200, y: 640 }, hole: { x: 60, y: 64 }, obstacles: [{ x: 290, y: 250, w: 34, h: 34 }, { x: 330, y: 450, w: 34, h: 34 }], openEdges: [3], courseShape: [[{"x":20,"y":20},{"x":380,"y":20},{"x":380,"y":680},{"x":20,"y":680}],[{"x":100,"y":160},{"x":260,"y":160},{"x":260,"y":540},{"x":100,"y":540}]], chamfers: [{ poly: 0, vertex: 2, size: 40 }] },
    { name: "Switchback", par: 3, ball: { x: 120, y: 620 }, hole: { x: 100, y: 60 }, obstacles: [{ x: 280, y: 244, w: 34, h: 34 }, { x: 50, y: 120, w: 34, h: 34 }], openEdges: [4], courseShape: [{"x":20,"y":20},{"x":260,"y":20},{"x":260,"y":200},{"x":380,"y":200},{"x":380,"y":332},{"x":220,"y":332},{"x":220,"y":680},{"x":20,"y":680},{"x":20,"y":520},{"x":120,"y":520},{"x":120,"y":200},{"x":20,"y":200}] },
    { name: "Cove", par: 3, ball: { x: 80, y: 600 }, hole: { x: 320, y: 80 }, obstacles: [{ x: 60, y: 340, w: 34, h: 34 }, { x: 100, y: 100, w: 34, h: 34 }], openEdges: [2], courseShape: [[{"x":20,"y":20},{"x":388,"y":20},{"x":386,"y":140},{"x":386,"y":560},{"x":380,"y":680},{"x":20,"y":680}],[{"x":160,"y":140},{"x":346,"y":140},{"x":346,"y":560},{"x":160,"y":560}]], chamfers: [{ poly: 0, vertex: 4, size: 40 }, { poly: 0, vertex: 3, size: 40 }] },
    { name: "Chicane", par: 3, ball: { x: 300, y: 620 }, hole: { x: 100, y: 80 }, obstacles: [{ x: 280, y: 200, w: 34, h: 34 }, { x: 170, y: 420, w: 34, h: 34 }], openEdges: [1, 7], courseShape: [{"x":20,"y":20},{"x":380,"y":20},{"x":380,"y":280},{"x":236,"y":280},{"x":236,"y":380},{"x":380,"y":380},{"x":380,"y":680},{"x":108,"y":680},{"x":108,"y":380},{"x":166,"y":380},{"x":166,"y":280},{"x":20,"y":280}] },
    { name: "Hook", par: 3, ball: { x: 70, y: 624 }, hole: { x: 320, y: 360 }, obstacles: [{ x: 302, y: 406, w: 34, h: 34 }, { x: 76, y: 218, w: 34, h: 34 }], openEdges: [4], courseShape: [{"x":20,"y":20},{"x":180,"y":20},{"x":180,"y":280},{"x":380,"y":280},{"x":380,"y":480},{"x":260,"y":480},{"x":260,"y":380},{"x":120,"y":380},{"x":120,"y":680},{"x":20,"y":680}] },
    { name: "Cross", par: 3, ball: { x: 200, y: 620 }, hole: { x: 200, y: 80 }, obstacles: [{ x: 180, y: 350, w: 34, h: 34 }, { x: 100, y: 290, w: 34, h: 34 }], openEdges: [0, 2, 4, 6], courseShape: [{"x":140,"y":20},{"x":260,"y":20},{"x":260,"y":220},{"x":380,"y":220},{"x":380,"y":338},{"x":286,"y":338},{"x":286,"y":680},{"x":140,"y":680},{"x":140,"y":420},{"x":20,"y":420},{"x":20,"y":220},{"x":140,"y":220}] },
    { name: "Arena", par: 3, ball: { x: 200, y: 620 }, hole: { x: 80, y: 420 }, obstacles: [{ x: 160, y: 302, w: 34, h: 34 }, { x: 280, y: 340, w: 34, h: 34 }, { x: 160, y: 100, w: 34, h: 34 }], openEdges: [0, 3, 6, 9], courseShape: [{"x":140,"y":20},{"x":260,"y":20},{"x":260,"y":220},{"x":380,"y":220},{"x":380,"y":460},{"x":260,"y":460},{"x":260,"y":680},{"x":140,"y":680},{"x":140,"y":460},{"x":20,"y":460},{"x":20,"y":220},{"x":140,"y":220}] },
    { name: "Narrows", par: 3, ball: { x: 200, y: 600 }, hole: { x: 114, y: 78 }, obstacles: [{ x: 100, y: 150, w: 34, h: 34 }], openEdges: [3, 9], courseShape: [{"x":40,"y":20},{"x":360,"y":20},{"x":360,"y":250},{"x":324,"y":250},{"x":324,"y":450},{"x":360,"y":450},{"x":360,"y":680},{"x":40,"y":680},{"x":40,"y":450},{"x":234,"y":450},{"x":234,"y":250},{"x":40,"y":250}] },
    { name: "Staircase", par: 3, ball: { x: 80, y: 620 }, hole: { x: 320, y: 80 }, obstacles: [{ x: 260, y: 180, w: 34, h: 34 }, { x: 66, y: 472, w: 34, h: 34 }], openEdges: [2, 6], courseShape: [{"x":200,"y":20},{"x":380,"y":20},{"x":380,"y":240},{"x":300,"y":240},{"x":300,"y":340},{"x":380,"y":340},{"x":380,"y":540},{"x":200,"y":540},{"x":200,"y":680},{"x":20,"y":680},{"x":20,"y":420},{"x":188,"y":420},{"x":188,"y":340},{"x":52,"y":340},{"x":52,"y":160},{"x":200,"y":160}], chamfers: [{ vertex: 0, size: 40 }, { vertex: 5, size: 40 }, { vertex: 10, size: 40 }, { vertex: 14, size: 40 }, { vertex: 13, size: 40 }] },
    { name: "The Catwalk", par: 3, ball: { x: 200, y: 600 }, hole: { x: 200, y: 80 }, obstacles: [{ x: 212, y: 192, w: 34, h: 34 }, { x: 100, y: 500, w: 34, h: 34 }], openEdges: [1, 7], courseShape: [{"x":130,"y":20},{"x":270,"y":20},{"x":270,"y":400},{"x":380,"y":400},{"x":380,"y":680},{"x":20,"y":680},{"x":20,"y":400},{"x":130,"y":400}] },
    { name: "The Wrap", par: 3, ball: { x: 320, y: 620 }, hole: { x: 80, y: 80 }, obstacles: [{ x: 55, y: 340, w: 34, h: 34 }, { x: 300, y: 200, w: 34, h: 34 }], openEdges: [0], courseShape: [[{"x":20,"y":20},{"x":380,"y":20},{"x":380,"y":680},{"x":20,"y":680}],[{"x":140,"y":160},{"x":260,"y":160},{"x":260,"y":360},{"x":200,"y":360},{"x":200,"y":520},{"x":140,"y":520}]], chamfers: [{ vertex: 3, size: 40 }] },
    { name: "Pinball", par: 3, ball: { x: 200, y: 120 }, hole: { x: 200, y: 580 }, obstacles: [{ x: 136, y: 246, w: 34, h: 34 }, { x: 240, y: 220, w: 34, h: 34 }, { x: 183, y: 320, w: 34, h: 34 }, { x: 96, y: 382, w: 34, h: 34 }, { x: 270, y: 420, w: 34, h: 34 }, { x: 180, y: 474, w: 34, h: 34 }], openEdges: [4], courseShape: [{"x":80,"y":20},{"x":320,"y":20},{"x":320,"y":160},{"x":380,"y":160},{"x":380,"y":680},{"x":20,"y":680},{"x":20,"y":160},{"x":80,"y":160}] },
    { name: "Backstop", par: 3, ball: { x: 300, y: 580 }, hole: { x: 60, y: 80 }, obstacles: [{ x: 140, y: 80, w: 34, h: 34 }, { x: 188, y: 312, w: 34, h: 34 }, { x: 246, y: 492, w: 34, h: 34 }], openEdges: [3], courseShape: [{"x":20,"y":20},{"x":300,"y":20},{"x":300,"y":200},{"x":240,"y":200},{"x":240,"y":460},{"x":380,"y":460},{"x":380,"y":680},{"x":100,"y":680},{"x":100,"y":460},{"x":140,"y":460},{"x":140,"y":200},{"x":20,"y":200}] },
    { name: "The Fork", par: 3, ball: { x: 200, y: 620 }, hole: { x: 60, y: 80 }, obstacles: [{ x: 300, y: 160, w: 34, h: 34 }, { x: 183, y: 360, w: 34, h: 34 }], openEdges: [0, 4], courseShape: [[{"x":20,"y":20},{"x":380,"y":20},{"x":380,"y":400},{"x":260,"y":400},{"x":260,"y":680},{"x":140,"y":680},{"x":140,"y":400},{"x":20,"y":400}],[{"x":160,"y":40},{"x":240,"y":40},{"x":240,"y":320},{"x":160,"y":320}]] },
    { name: "Serpentine", par: 3, ball: { x: 300, y: 620 }, hole: { x: 100, y: 80 }, obstacles: [{ x: 140, y: 180, w: 34, h: 34 }, { x: 250, y: 440, w: 34, h: 34 }], openEdges: [], courseShape: [{"x":20,"y":20},{"x":220,"y":20},{"x":220,"y":300},{"x":380,"y":300},{"x":380,"y":680},{"x":180,"y":680},{"x":180,"y":380},{"x":20,"y":380}], chamfers: [{ vertex: 3, size: 40 }, { vertex: 7, size: 40 }] },
    { name: "The Island", par: 3, ball: { x: 60, y: 620 }, hole: { x: 320, y: 80 }, obstacles: [{ x: 300, y: 400, w: 34, h: 34 }, { x: 60, y: 300, w: 34, h: 34 }], openEdges: [0], courseShape: [[{"x":20,"y":20},{"x":380,"y":20},{"x":380,"y":680},{"x":20,"y":680}],[{"x":140,"y":240},{"x":260,"y":240},{"x":260,"y":440},{"x":140,"y":440}]] },
    { name: "Hairpin", par: 3, ball: { x: 100, y: 600 }, hole: { x: 300, y: 600 }, obstacles: [{ x: 100, y: 140, w: 34, h: 34 }, { x: 280, y: 320, w: 34, h: 34 }], openEdges: [0], courseShape: [{"x":20,"y":20},{"x":380,"y":20},{"x":380,"y":680},{"x":220,"y":680},{"x":220,"y":200},{"x":180,"y":200},{"x":180,"y":680},{"x":20,"y":680}] },
    { name: "The Trick Shot", par: 3, ball: { x: 200, y: 560 }, hole: { x: 92, y: 198 }, obstacles: [{ x: 223, y: 383, w: 34, h: 34 }], openEdges: [], courseShape: [{"x":40,"y":40},{"x":360,"y":40},{"x":360,"y":260},{"x":280,"y":260},{"x":280,"y":660},{"x":142,"y":660},{"x":142,"y":260},{"x":40,"y":260}] },
    { name: "The Bank Shot", par: 3, ball: { x: 80, y: 550 }, hole: { x: 320, y: 550 }, obstacles: [{ x: 183, y: 180, w: 34, h: 34 }, { x: 183, y: 290, w: 34, h: 34 }, { x: 183, y: 400, w: 34, h: 34 }, { x: 183, y: 510, w: 34, h: 34 }], openEdges: [], courseShape: [{"x":40,"y":40},{"x":360,"y":40},{"x":360,"y":660},{"x":40,"y":660}], chamfers: [{ vertex: 2, size: 40 }, { vertex: 3, size: 40 }] },
  ],
  hard: [
    { name: "Labyrinth", par: 4, ball: { x: 72, y: 646 }, hole: { x: 320, y: 60 }, obstacles: [{ x: 260, y: 120, w: 34, h: 34 }, { x: 140, y: 230, w: 34, h: 34 }, { x: 105, y: 420, w: 34, h: 34 }, { x: 272, y: 386, w: 34, h: 34 }], openEdges: [2, 6], courseShape: [{"x":100,"y":20},{"x":380,"y":20},{"x":380,"y":180},{"x":200,"y":180},{"x":200,"y":280},{"x":380,"y":280},{"x":380,"y":454},{"x":200,"y":454},{"x":200,"y":540},{"x":380,"y":540},{"x":380,"y":680},{"x":20,"y":680},{"x":20,"y":540},{"x":80,"y":540},{"x":80,"y":280},{"x":20,"y":280},{"x":20,"y":20}] },
    { name: "Gauntlet", par: 4, ball: { x: 200, y: 640 }, hole: { x: 164, y: 62 }, obstacles: [{ x: 110, y: 580, w: 34, h: 34 }, { x: 280, y: 530, w: 34, h: 34 }, { x: 100, y: 430, w: 34, h: 34 }, { x: 270, y: 380, w: 34, h: 34 }, { x: 158, y: 280, w: 34, h: 34 }, { x: 250, y: 230, w: 34, h: 34 }], openEdges: [1], courseShape: [{"x":80,"y":20},{"x":320,"y":20},{"x":320,"y":100},{"x":360,"y":100},{"x":360,"y":300},{"x":380,"y":300},{"x":380,"y":680},{"x":20,"y":680},{"x":20,"y":300},{"x":108,"y":300},{"x":108,"y":100},{"x":80,"y":100}] },
    { name: "Fortress", par: 4, ball: { x: 336, y: 646 }, hole: { x: 74, y: 82 }, obstacles: [{ x: 305, y: 200, w: 34, h: 34 }, { x: 280, y: 450, w: 34, h: 34 }, { x: 152, y: 332, w: 34, h: 34 }], openEdges: [3], courseShape: [[{"x":48,"y":20},{"x":380,"y":20},{"x":380,"y":680},{"x":48,"y":680}],[{"x":100,"y":140},{"x":250,"y":140},{"x":250,"y":300},{"x":100,"y":300}],[{"x":100,"y":400},{"x":250,"y":400},{"x":250,"y":560},{"x":100,"y":560}]], chamfers: [{ poly: 0, vertex: 3, size: 40 }, { poly: 0, vertex: 1, size: 40 }] },
    { name: "Corridors", par: 4, ball: { x: 60, y: 630 }, hole: { x: 328, y: 346 }, obstacles: [{ x: 100, y: 200, w: 34, h: 34 }, { x: 100, y: 400, w: 34, h: 34 }, { x: 332, y: 36, w: 34, h: 34 }], openEdges: [2], courseShape: [{"x":20,"y":20},{"x":380,"y":20},{"x":380,"y":400},{"x":280,"y":400},{"x":280,"y":200},{"x":240,"y":200},{"x":240,"y":680},{"x":20,"y":680}], chamfers: [{ vertex: 0, size: 40 }] },
    { name: "Spiral", par: 4, ball: { x: 60, y: 640 }, hole: { x: 232, y: 536 }, obstacles: [{ x: 300, y: 130, w: 34, h: 34 }, { x: 76, y: 338, w: 34, h: 34 }, { x: 300, y: 520, w: 34, h: 34 }], openEdges: [8], courseShape: [{"x":20,"y":20},{"x":380,"y":20},{"x":380,"y":600},{"x":146,"y":600},{"x":146,"y":142},{"x":280,"y":142},{"x":280,"y":460},{"x":100,"y":460},{"x":100,"y":680},{"x":20,"y":680}], chamfers: [{ vertex: 1, size: 40 }, { vertex: 2, size: 40 }, { vertex: 3, size: 40 }] },
    { name: "Archipelago", par: 4, ball: { x: 60, y: 620 }, hole: { x: 340, y: 60 }, obstacles: [{ x: 55, y: 260, w: 34, h: 34 }, { x: 305, y: 460, w: 34, h: 34 }], openEdges: [0], courseShape: [[{"x":20,"y":20},{"x":380,"y":20},{"x":380,"y":680},{"x":20,"y":680}],[{"x":100,"y":140},{"x":200,"y":140},{"x":200,"y":240},{"x":100,"y":240}],[{"x":200,"y":340},{"x":300,"y":340},{"x":300,"y":440},{"x":200,"y":440}],[{"x":80,"y":460},{"x":180,"y":460},{"x":180,"y":560},{"x":80,"y":560}]] },
    { name: "Razor Ridge", par: 4, ball: { x: 60, y: 640 }, hole: { x: 320, y: 60 }, obstacles: [{ x: 310, y: 190, w: 34, h: 34 }, { x: 80, y: 400, w: 34, h: 34 }], openEdges: [2, 3, 4, 8, 10], courseShape: [{"x":240,"y":20},{"x":380,"y":20},{"x":380,"y":260},{"x":260,"y":260},{"x":260,"y":320},{"x":380,"y":320},{"x":380,"y":520},{"x":180,"y":520},{"x":180,"y":460},{"x":120,"y":460},{"x":120,"y":680},{"x":20,"y":680},{"x":20,"y":320},{"x":140,"y":320},{"x":140,"y":160},{"x":240,"y":160}], chamfers: [{ vertex: 12, size: 40 }] },
    { name: "Fortress Moat", par: 5, ball: { x: 60, y: 620 }, hole: { x: 318, y: 82 }, obstacles: [{ x: 54, y: 330, w: 34, h: 34 }, { x: 296, y: 308, w: 34, h: 34 }, { x: 204, y: 366, w: 34, h: 34 }], openEdges: [2, 6, 10, 14], courseShape: [{"x":46,"y":20},{"x":380,"y":20},{"x":380,"y":240},{"x":194,"y":240},{"x":194,"y":280},{"x":380,"y":280},{"x":380,"y":420},{"x":280,"y":420},{"x":280,"y":460},{"x":220,"y":460},{"x":220,"y":680},{"x":20,"y":680},{"x":20,"y":460},{"x":120,"y":460},{"x":120,"y":420},{"x":20,"y":420},{"x":20,"y":280},{"x":92,"y":280},{"x":92,"y":240},{"x":46,"y":240}] },
    { name: "The Pier", par: 4, ball: { x: 100, y: 600 }, hole: { x: 340, y: 350 }, obstacles: [{ x: 240, y: 300, w: 34, h: 34 }, { x: 240, y: 364, w: 34, h: 34 }, { x: 100, y: 200, w: 34, h: 34 }], openEdges: [2, 3, 4], courseShape: [{"x":20,"y":20},{"x":180,"y":20},{"x":180,"y":280},{"x":380,"y":280},{"x":380,"y":420},{"x":180,"y":420},{"x":180,"y":680},{"x":20,"y":680}] },
    { name: "The Tiers", par: 4, ball: { x: 200, y: 620 }, hole: { x: 200, y: 80 }, obstacles: [{ x: 280, y: 490, w: 34, h: 34 }, { x: 160, y: 300, w: 34, h: 34 }, { x: 230, y: 380, w: 34, h: 34 }], openEdges: [1, 3, 9, 11], courseShape: [{"x":160,"y":20},{"x":240,"y":20},{"x":240,"y":200},{"x":300,"y":200},{"x":300,"y":440},{"x":360,"y":440},{"x":360,"y":680},{"x":40,"y":680},{"x":40,"y":440},{"x":100,"y":440},{"x":100,"y":200},{"x":160,"y":200}] },
    { name: "The Grid", par: 4, ball: { x: 60, y: 620 }, hole: { x: 340, y: 60 }, obstacles: [{ x: 120, y: 160, w: 34, h: 34 }, { x: 240, y: 160, w: 34, h: 34 }, { x: 60, y: 310, w: 34, h: 34 }, { x: 180, y: 310, w: 34, h: 34 }, { x: 300, y: 310, w: 34, h: 34 }, { x: 240, y: 460, w: 34, h: 34 }], openEdges: [0, 2], courseShape: [{"x":20,"y":20},{"x":380,"y":20},{"x":380,"y":680},{"x":20,"y":680}], chamfers: [{ vertex: 0, size: 40 }] },
    { name: "The Chasm", par: 4, ball: { x: 80, y: 620 }, hole: { x: 320, y: 80 }, obstacles: [{ x: 164, y: 328, w: 34, h: 34 }, { x: 300, y: 200, w: 34, h: 34 }, { x: 60, y: 420, w: 34, h: 34 }], openEdges: [2, 8], courseShape: [{"x":20,"y":20},{"x":140,"y":20},{"x":140,"y":300},{"x":260,"y":300},{"x":260,"y":20},{"x":380,"y":20},{"x":380,"y":680},{"x":260,"y":680},{"x":260,"y":400},{"x":140,"y":400},{"x":140,"y":680},{"x":20,"y":680}] },
    { name: "Double Island", par: 4, ball: { x: 60, y: 620 }, hole: { x: 340, y: 60 }, obstacles: [{ x: 55, y: 180, w: 34, h: 34 }, { x: 305, y: 480, w: 34, h: 34 }, { x: 244, y: 286, w: 34, h: 34 }], openEdges: [0], courseShape: [[{"x":20,"y":20},{"x":380,"y":20},{"x":380,"y":680},{"x":20,"y":680}],[{"x":120,"y":120},{"x":220,"y":120},{"x":220,"y":260},{"x":120,"y":260}],[{"x":180,"y":420},{"x":280,"y":420},{"x":280,"y":560},{"x":180,"y":560}]] },
    { name: "The Maze", par: 5, ball: { x: 60, y: 620 }, hole: { x: 320, y: 80 }, obstacles: [{ x: 280, y: 120, w: 34, h: 34 }, { x: 120, y: 280, w: 34, h: 34 }, { x: 280, y: 440, w: 34, h: 34 }, { x: 60, y: 388, w: 34, h: 34 }], openEdges: [], courseShape: [{"x":200,"y":20},{"x":380,"y":20},{"x":380,"y":200},{"x":160,"y":200},{"x":160,"y":340},{"x":380,"y":340},{"x":380,"y":520},{"x":160,"y":520},{"x":160,"y":680},{"x":20,"y":680},{"x":20,"y":340},{"x":70,"y":340},{"x":70,"y":140},{"x":200,"y":140}], chamfers: [{ vertex: 12, size: 40 }, { vertex: 8, size: 40 }, { vertex: 2, size: 40 }] },
    { name: "Corkscrew", par: 4, ball: { x: 100, y: 620 }, hole: { x: 100, y: 60 }, obstacles: [{ x: 60, y: 200, w: 34, h: 34 }, { x: 300, y: 340, w: 34, h: 34 }], openEdges: [0, 4, 8], courseShape: [{"x":20,"y":20},{"x":240,"y":20},{"x":240,"y":240},{"x":380,"y":240},{"x":380,"y":460},{"x":160,"y":460},{"x":160,"y":680},{"x":20,"y":680},{"x":20,"y":440},{"x":104,"y":440},{"x":104,"y":260},{"x":20,"y":260}], chamfers: [{ vertex: 5, size: 40 }] },
    { name: "The Carom", par: 4, ball: { x: 80, y: 600 }, hole: { x: 328, y: 150 }, obstacles: [{ x: 200, y: 300, w: 34, h: 34 }, { x: 120, y: 160, w: 34, h: 34 }], openEdges: [0], courseShape: [{"x":20,"y":20},{"x":380,"y":20},{"x":380,"y":200},{"x":280,"y":200},{"x":280,"y":104},{"x":240,"y":104},{"x":240,"y":260},{"x":380,"y":260},{"x":380,"y":680},{"x":20,"y":680}] },
    { name: "Off the Rails", par: 4, ball: { x: 60, y: 80 }, hole: { x: 120, y: 600 }, obstacles: [{ x: 180, y: 140, w: 34, h: 34 }, { x: 260, y: 350, w: 34, h: 34 }, { x: 80, y: 560, w: 34, h: 34 }], openEdges: [1, 4, 9], courseShape: [{"x":20,"y":20},{"x":240,"y":20},{"x":240,"y":260},{"x":380,"y":260},{"x":380,"y":460},{"x":240,"y":460},{"x":240,"y":680},{"x":20,"y":680},{"x":20,"y":460},{"x":160,"y":460},{"x":160,"y":260},{"x":20,"y":260}] },
  ],
};

/** Fisher-Yates shuffle (returns new array) */
function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Curated hole sequences. Every player on a campaign using a preset plays
// these exact holes in this order, so leaderboard scores are directly
// comparable. Edit this list (or the underlying HOLE_POOL) to change what
// the campaign owner can choose from. Hole references use `name` so they
// survive reordering of HOLE_POOL.
export const HOLE_PRESETS = [
  {
    id: 'quick',
    label: 'Quick round',
    description: 'Three short holes — good for a story drop or QR scan.',
    holes: ['Wide Bend', 'T-Green', 'Dog Leg'],
  },
  {
    id: 'classic',
    label: 'Classic',
    description: 'Five holes ramping easy → medium → hard.',
    holes: ['Wide Bend', 'Notch', 'Dog Leg', 'Chicane', 'Labyrinth'],
  },
  {
    id: 'tournament',
    label: 'Tournament',
    description: 'Nine holes, full mixed-difficulty progression.',
    holes: ['Wide Bend', 'Peninsula', 'Alcove', 'Dog Leg', 'Switchback', 'Hook', 'Labyrinth', 'Gauntlet', 'Fortress'],
  },
];

// Find a hole by name across all tiers. Returns null if not found (e.g. the
// hole was renamed or removed — the preset will then skip it).
function findHoleByName(name) {
  for (const tier of ['easy', 'medium', 'hard']) {
    const found = HOLE_POOL[tier].find((h) => h.name === name);
    if (found) return found;
  }
  return null;
}

function getPresetById(id) {
  return HOLE_PRESETS.find((p) => p.id === id) || null;
}

/** Minimum clearance (centre-to-centre) between an obstacle and ball/hole */
const OBS_CLEARANCE = 55; // ~BALL_R + OBS_SIZE + margin

/** Validate obstacles — remove any that overlap the hole, ball start, are outside
 *  the course, or block a narrow passage (ball can't get around). */
function validateObstacles(hole) {
  if (!hole.obstacles || hole.obstacles.length === 0) return hole;
  const bx = hole.ball.x, by = hole.ball.y;
  const hx = hole.hole.x, hy = hole.hole.y;
  const shape = hole.courseShape;
  const passageW = BALL_R * 2 + 6; // ball diameter + small margin
  const valid = hole.obstacles.filter(ob => {
    const cx = ob.x + ob.w / 2;
    const cy = ob.y + ob.h / 2;
    // Check all 4 corners + centre are inside the course shape
    if (shape) {
      const pts = [
        [ob.x, ob.y], [ob.x + ob.w, ob.y],
        [ob.x, ob.y + ob.h], [ob.x + ob.w, ob.y + ob.h],
        [cx, cy],
      ];
      for (const [px, py] of pts) {
        if (!pointInCourseShape(px, py, shape)) return false;
      }
      // Ensure ball can pass on at least 2 sides (prevents blocking corridors)
      // Check left/right passage at three y-levels (top, centre, bottom of obstacle)
      // to catch corridors that narrow at the obstacle edges
      const yTop = ob.y + ob.h * 0.25;
      const yBot = ob.y + ob.h * 0.75;
      const leftPassable =
        pointInCourseShape(ob.x - passageW, cy, shape) &&
        pointInCourseShape(ob.x - passageW, yTop, shape) &&
        pointInCourseShape(ob.x - passageW, yBot, shape);
      const rightPassable =
        pointInCourseShape(ob.x + ob.w + passageW, cy, shape) &&
        pointInCourseShape(ob.x + ob.w + passageW, yTop, shape) &&
        pointInCourseShape(ob.x + ob.w + passageW, yBot, shape);
      const abovePassable = pointInCourseShape(cx, ob.y - passageW, shape);
      const belowPassable = pointInCourseShape(cx, ob.y + ob.h + passageW, shape);
      const passableSides = [abovePassable, belowPassable, leftPassable, rightPassable];
      if (passableSides.filter(Boolean).length < 2) return false;
    }
    // Too close to ball start?
    const dbx = cx - bx, dby = cy - by;
    if (Math.sqrt(dbx * dbx + dby * dby) < OBS_CLEARANCE) return false;
    // Too close to (or covering) the hole?
    const dhx = cx - hx, dhy = cy - hy;
    if (Math.sqrt(dhx * dhx + dhy * dhy) < OBS_CLEARANCE) return false;
    return true;
  });
  return { ...hole, obstacles: valid };
}

/** Apply chamfered corners — replaces a sharp 90° vertex with a 45° diagonal.
 *  Processes descending by index so splicing doesn't shift pending chamfers.
 *  Also adjusts openEdges indices to account for inserted vertices. */
export function chamferPoly(poly, chamfers) {
  const sorted = [...chamfers].sort((a, b) => b.vertex - a.vertex);
  const newPoly = [...poly.map(v => ({ ...v }))];
  for (const ch of sorted) {
    const vi = ch.vertex;
    const size = ch.size || 20;
    const n = newPoly.length;
    const V = newPoly[vi];
    const A = newPoly[(vi - 1 + n) % n];
    const B = newPoly[(vi + 1) % n];
    const dAx = A.x - V.x, dAy = A.y - V.y;
    const dAlen = Math.sqrt(dAx * dAx + dAy * dAy) || 1;
    const dBx = B.x - V.x, dBy = B.y - V.y;
    const dBlen = Math.sqrt(dBx * dBx + dBy * dBy) || 1;
    const V1 = { x: V.x + (dAx / dAlen) * size, y: V.y + (dAy / dAlen) * size };
    const V2 = { x: V.x + (dBx / dBlen) * size, y: V.y + (dBy / dBlen) * size };
    newPoly.splice(vi, 1, V1, V2);
  }
  return newPoly;
}

export function applyChamfers(hole) {
  if (!hole.chamfers || hole.chamfers.length === 0) return hole;
  const shape = hole.courseShape;
  if (!shape) return hole;
  const isCompound = Array.isArray(shape[0]);

  if (!isCompound) {
    // Simple shape — chamfers target the single polygon, adjust openEdges
    const sorted = [...hole.chamfers].sort((a, b) => b.vertex - a.vertex);
    const newShape = chamferPoly(shape, hole.chamfers);
    let openEdges = [...(hole.openEdges || [])];
    for (const ch of sorted) {
      openEdges = openEdges.map(oe => oe >= ch.vertex ? oe + 1 : oe);
    }
    return { ...hole, courseShape: newShape, openEdges };
  }

  // Compound shape — chamfers use `poly` (default 0) to target a sub-polygon
  const newShape = shape.map(p => [...p.map(v => ({ ...v }))]);
  const grouped = {};
  for (const ch of hole.chamfers) {
    const pi = ch.poly || 0;
    if (!grouped[pi]) grouped[pi] = [];
    grouped[pi].push(ch);
  }
  for (const pi of Object.keys(grouped)) {
    newShape[pi] = chamferPoly(newShape[pi], grouped[pi]);
  }
  // Adjust openEdges for outer polygon (poly 0) chamfers
  let openEdges = [...(hole.openEdges || [])];
  if (grouped[0]) {
    const sorted = [...grouped[0]].sort((a, b) => b.vertex - a.vertex);
    for (const ch of sorted) {
      openEdges = openEdges.map(oe => oe >= ch.vertex ? oe + 1 : oe);
    }
  }
  return { ...hole, courseShape: newShape, openEdges };
}

/** Pick holes — shuffled to guarantee no duplicates within a game */
// Pick holes for a play session. When `presetId` is set, returns the curated
// sequence from HOLE_PRESETS so every player on that campaign plays the same
// course. Otherwise falls back to the random pick (count + difficulty filter).
function pickHoles(holeCount = 3, difficulty = 'mixed', presetId = null) {
  if (presetId) {
    const preset = getPresetById(presetId);
    if (preset) {
      const resolved = preset.holes
        .map((name) => findHoleByName(name))
        .filter(Boolean);
      if (resolved.length > 0) {
        return resolved.map((raw) => validateObstacles(applyChamfers(raw)));
      }
      // Fall through to random if every preset hole was renamed/removed.
    }
  }
  if (difficulty === 'mixed') {
    const tiers = ['easy', 'medium', 'hard'];
    const shuffled = {
      easy: shuffle(HOLE_POOL.easy),
      medium: shuffle(HOLE_POOL.medium),
      hard: shuffle(HOLE_POOL.hard),
    };
    const idx = { easy: 0, medium: 0, hard: 0 };
    const holes = [];
    for (let i = 0; i < holeCount; i++) {
      const tier = tiers[i % tiers.length];
      const raw = shuffled[tier][idx[tier] % shuffled[tier].length];
      const prepared = validateObstacles(applyChamfers(raw));
      holes.push(prepared);
      idx[tier]++;
    }
    return holes;
  }
  const pool = shuffle(HOLE_POOL[difficulty] || HOLE_POOL.easy);
  const holes = [];
  for (let i = 0; i < holeCount; i++) {
    const raw = pool[i % pool.length];
    const prepared = validateObstacles(applyChamfers(raw));
    holes.push(prepared);
  }
  return holes;
}

function calcHolePoints(strokes, elapsedSecs) {
  const timePenalty = Math.floor(elapsedSecs) * POINTS_TIME_PENALTY;
  const strokePenalty = strokes * POINTS_STROKE_COST;
  return Math.max(POINTS_MIN, POINTS_BASE - strokePenalty - timePenalty);
}

// ── Circle vs AABB collision ───────────────────────────────
function circleRect(cx, cy, r, rx, ry, rw, rh) {
  const closestX = Math.max(rx, Math.min(cx, rx + rw));
  const closestY = Math.max(ry, Math.min(cy, ry + rh));
  const dx = cx - closestX;
  const dy = cy - closestY;
  const d2 = dx * dx + dy * dy;

  // Ball centre is OUTSIDE the rect — standard closest-point check
  if (d2 > 0.0001 && d2 < r * r) {
    const d = Math.sqrt(d2);
    return { hit: true, nx: dx / d, ny: dy / d, overlap: r - d };
  }

  // Ball centre is INSIDE the rect — find nearest edge and push out
  if (cx >= rx && cx <= rx + rw && cy >= ry && cy <= ry + rh) {
    const dLeft = cx - rx;
    const dRight = (rx + rw) - cx;
    const dTop = cy - ry;
    const dBottom = (ry + rh) - cy;
    const minDist = Math.min(dLeft, dRight, dTop, dBottom);
    let nx = 0, ny = 0;
    if (minDist === dLeft)       { nx = -1; }
    else if (minDist === dRight) { nx =  1; }
    else if (minDist === dTop)   { ny = -1; }
    else                         { ny =  1; }
    return { hit: true, nx, ny, overlap: r + minDist };
  }

  return null;
}

// ── Point-in-polygon (ray-casting) ─────────────────────────
function pointInPoly(px, py, poly) {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i].x, yi = poly[i].y;
    const xj = poly[j].x, yj = poly[j].y;
    if (((yi > py) !== (yj > py)) &&
        (px < (xj - xi) * (py - yi) / (yj - yi) + xi)) {
      inside = !inside;
    }
  }
  return inside;
}
// For compound shapes: inside outer, outside all cutouts
function pointInCourseShape(px, py, shape) {
  if (!shape || shape.length === 0) return true;
  const polys = Array.isArray(shape[0]) ? shape : [shape];
  if (!pointInPoly(px, py, polys[0])) return false;
  for (let i = 1; i < polys.length; i++) {
    if (pointInPoly(px, py, polys[i])) return false;
  }
  return true;
}

// ── Circle vs line segment collision (polygon boundary) ───
// tMin/tMax allow excluding endpoints (0=start, 1=end of segment)
function circleSegment(cx, cy, r, x1, y1, x2, y2, tMin = 0, tMax = 1) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return null;
  let t = ((cx - x1) * dx + (cy - y1) * dy) / len2;
  t = Math.max(tMin, Math.min(tMax, t));
  const closestX = x1 + t * dx;
  const closestY = y1 + t * dy;
  const distX = cx - closestX;
  const distY = cy - closestY;
  const dist2 = distX * distX + distY * distY;
  if (dist2 < r * r && dist2 > 0.0001) {
    const dist = Math.sqrt(dist2);
    return { hit: true, nx: distX / dist, ny: distY / dist, overlap: r - dist };
  }
  return null;
}

// ── Ray vs line-segment intersection (for aim trajectory preview) ──
function raySegHit(ox, oy, dx, dy, x1, y1, x2, y2) {
  const ex = x2 - x1, ey = y2 - y1;
  const denom = dx * ey - dy * ex;
  if (Math.abs(denom) < 0.0001) return null;
  const t = ((x1 - ox) * ey - (y1 - oy) * ex) / denom;
  const u = ((x1 - ox) * dy - (y1 - oy) * dx) / denom;
  if (t > BALL_R && u >= 0 && u <= 1) {
    const len = Math.sqrt(ex * ex + ey * ey);
    if (len < 0.001) return null;
    let nx = -ey / len, ny = ex / len;
    if (nx * dx + ny * dy > 0) { nx = -nx; ny = -ny; }
    return { t, nx, ny };
  }
  return null;
}

// ── Image loader helper ────────────────────────────────────
function loadImg(src) {
  if (!src) return null;
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.src = src;
  return img;
}

// ════════════════════════════════════════════════════════════
const Game = ({ game, onComplete, onGameplayStart, onScoreChange, settings }) => {
  const { loadFont } = useFont();
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const ctxRef = useRef(null);

  // Transform refs
  const scaleRef = useRef(1);
  const offRef = useRef({ x: 0, y: 0 });
  const dprRef = useRef(1);
  const vwRef = useRef(REF_VIRTUAL_WIDTH);
  const vhRef = useRef(REF_VIRTUAL_HEIGHT);

  // Randomly selected holes for this game. settings.customHoles is an admin-
  // editor escape hatch — when provided, those holes are used verbatim.
  // settings.holePresetId selects a curated preset sequence from HOLE_PRESETS
  // so every player on that campaign plays the same course in the same order.
  // Otherwise we fall back to a random pick from HOLE_POOL.
  const holesRef = useRef(
    settings?.customHoles?.length
      ? settings.customHoles.map((h) => validateObstacles(applyChamfers(h)))
      : pickHoles(settings?.holeCount, settings?.difficulty, settings?.holePresetId)
  );
  const prevHoleSettings = useRef({ count: settings?.holeCount || 3, diff: settings?.difficulty || 'mixed', presetId: settings?.holePresetId || null });

  // Game state (all refs for 60fps perf)
  const ball = useRef({ x: 0, y: 0, vx: 0, vy: 0 });
  const ballRollAngle = useRef(0);
  const moving = useRef(false);
  const holeIdx = useRef(0);

  const strokes = useRef(0);
  const totalScore = useRef(0);
  const holeScores = useRef([]);
  const done = useRef(false);
  const gameActive = useRef(false);
  const ballSunk = useRef(false);

  // Aim state
  const aiming = useRef(false);
  const aimStart = useRef({ x: 0, y: 0 });
  const aimCur = useRef({ x: 0, y: 0 });

  // Animations
  const sinkAnim = useRef({ active: false, t: 0 });
  const fallAnim = useRef({ active: false, t: 0, x: 0, y: 0 });
  // (msgAnim removed — transitions go straight to crossfade)
  const fadeAnim = useRef({ active: false, t: 0 });
  const flashAnim = useRef({ active: false, t: 0, color: 'warm' });
  const shakeRef = useRef({ amount: 0, x: 0, y: 0 });
  const lastShotPos = useRef({ x: 0, y: 0 });
  const floatingTextsRef = useRef([]);
  const particlesRef = useRef([]);
  const frameTimeRef = useRef(0);
  const sparklesRef = useRef(null);
  const lastTimeRef = useRef(0);
  const dt60Ref = useRef(1);
  // (instructionShown removed — instructions now only in countdown overlay)

  // Per-hole countdown timer
  const holeTimerRef = useRef(DEFAULT_HOLE_TIME);
  const holeTimerRunning = useRef(false);
  const holeTimeLimitRef = useRef(DEFAULT_HOLE_TIME);
  const holeElapsedRef = useRef(0); // seconds elapsed this hole (for scoring)
  const timerElRef = useRef(null);    // DOM ref for off-canvas timer
  const holeInfoRef = useRef(null);   // DOM ref for hole progress HUD
  const floorStyleRef = useRef('checkerboard');

  // Audio
  const audioCtxRef = useRef(null);
  const audioPoolRef = useRef(null);

  // ── Theme from game options ────────────────────────────
  const colors = {
    background: game?.options?.customColors?.background || '#7EC8E3',
    surface: game?.options?.customColors?.surface || '#FFFFFF',
    primary: game?.options?.customColors?.primary || '#8B7355',
    text: game?.options?.customColors?.text || '#1F2937',
    primaryAction: game?.options?.customColors?.primaryAction || '#FFFFFF',
    secondaryAction: game?.options?.customColors?.secondaryAction || '#EF4444',
    buttonText: game?.options?.customColors?.buttonText || '#FFFFFF',
  };
  const primaryFont = game?.options?.theme?.typography?.primaryFont || 'Inter';
  const fontScale = game?.options?.theme?.typography?.fontScale;
  const frameStyle = game?.options?.frameStyle || 'clean';

  useEffect(() => { loadFont(primaryFont); }, [primaryFont, loadFont]);

  // ── Preloaded images ───────────────────────────────────
  const ballImgRef = useRef(null);
  const obstacleImgRef = useRef(null);
  const flagImgRef = useRef(null);
  const logoImgRef = useRef(null);
  const bgImgRef = useRef(null);
  const [imagesLoaded, setImagesLoaded] = useState(false);

  useEffect(() => {
    const pending = [];
    const track = (promise) => pending.push(promise);

    const loadTracked = (src, ref) => {
      if (src) {
        const img = loadImg(src);
        track(new Promise((resolve) => {
          img.onload = () => { ref.current = ensureMinResolution(img); resolve(); };
          img.onerror = () => { ref.current = null; resolve(); };
        }));
      } else { ref.current = null; }
    };

    loadTracked(settings?.ballImage, ballImgRef);
    loadTracked(settings?.wallImage || settings?.obstacleImage, obstacleImgRef);
    loadTracked(settings?.flagImage, flagImgRef);
    loadTracked(settings?.headlinePhoto, logoImgRef);
    const bgUrl = settings?.backgroundImage || settings?.gameBackgroundImage;
    if (!isProceduralBackground(bgUrl)) {
      loadTracked(bgUrl, bgImgRef);
    }

    if (pending.length === 0) {
      setImagesLoaded(true);
    } else {
      Promise.all(pending).then(() => setImagesLoaded(true));
    }
  }, [settings?.ballImage, settings?.wallImage, settings?.obstacleImage, settings?.flagImage, settings?.headlinePhoto, settings?.backgroundImage, settings?.gameBackgroundImage]);

  // ── Countdown / start sequence ─────────────────────────
  const {
    isLoading,
    countdown,
    gameStarted,
    showOverlay,
  } = useGameStartSequence({
    assetsLoaded: imagesLoaded,
    countdownFrom: 3,
    countdownInterval: 1000,
  });

  // Notify parent when gameplay starts (after countdown)
  useEffect(() => {
    if (gameStarted && countdown <= 0) {
      gameActive.current = true;
      if (!audioPoolRef.current) {
        audioPoolRef.current = createAudioPool(settings, GOLF_SOUND_MAP);
      }
      if (onGameplayStart) onGameplayStart();
      // Start the per-hole timer now that countdown is done
      holeTimerRunning.current = true;
    }
  }, [gameStarted, countdown, onGameplayStart]);

  // Recreate audio pool when sound settings change
  useEffect(() => {
    if (audioPoolRef.current) {
      audioPoolRef.current = createAudioPool(settings, GOLF_SOUND_MAP);
    }
  }, [settings?.collectSound, settings?.deathSound, settings?.bounceSound]);

  // Branding colors (ref for draw loop performance)
  const col = useRef({
    course: '#7BC67E', obstacle: '#8B7355',
    ball: '#FFFFFF', flag: '#EF4444', text: '#1F2937', surface: '#FFFFFF',
  });
  const cloudsRef = useRef(null);

  // Update holes when settings change (before game starts / in editor preview)
  const hc = settings?.holeCount || 3;
  const hd = settings?.difficulty || 'mixed';
  const hp = settings?.holePresetId || null;
  if (!gameActive.current && !done.current && !settings?.customHoles && (prevHoleSettings.current.count !== hc || prevHoleSettings.current.diff !== hd || prevHoleSettings.current.presetId !== hp)) {
    holesRef.current = pickHoles(hc, hd, hp);
    prevHoleSettings.current = { count: hc, diff: hd, presetId: hp };
  }

  // Update color ref on every render (no stale closure risk)
  const cc = settings?.customColors || {};
  col.current = {
    course: settings?.floorColor || '#7BC67E',
    obstacle: settings?.wallColor || cc.primary || '#8B7355',
    border: settings?.borderColor || '#E8DFD0',
    ball: settings?.ballColor || cc.primaryAction || '#FFFFFF',
    flag: cc.secondaryAction || '#EF4444',
    text: cc.text || '#1F2937',
    surface: cc.surface || '#FFFFFF',
  };
  floorStyleRef.current = settings?.floorStyle || 'checkerboard';
  holeTimeLimitRef.current = DEFAULT_HOLE_TIME; // Always 30s

  // ── Coordinate transforms ──────────────────────────────
  const toVirtual = useCallback((px, py) => {
    const s = scaleRef.current;
    const o = offRef.current;
    return { x: px / s - o.x, y: py / s - o.y };
  }, []);

  // ── Quick synth beep ───────────────────────────────────
  const beep = useCallback((freq, dur, vol = 0.15) => {
    try {
      if (!audioCtxRef.current) audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      const ac = audioCtxRef.current;
      const osc = ac.createOscillator();
      const gain = ac.createGain();
      osc.connect(gain);
      gain.connect(ac.destination);
      osc.frequency.value = freq;
      osc.type = 'sine';
      gain.gain.setValueAtTime(vol, ac.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + dur);
      osc.start();
      osc.stop(ac.currentTime + dur);
    } catch (_) { /* audio not available */ }
  }, []);

  // ── Resize canvas (FOV expansion — no letterboxing) ──
  const resize = useCallback(() => {
    const cvs = canvasRef.current;
    const container = containerRef.current;
    if (!cvs || !container) return;
    // Cap at 2 to match other engines — rendering at 3× on modern iPhones quadruples fill ops
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    dprRef.current = dpr;

    const containerWidth = container.clientWidth || REF_VIRTUAL_WIDTH;
    const containerHeight = container.clientHeight || REF_VIRTUAL_HEIGHT;

    const rawScale = Math.min(containerWidth / REF_VIRTUAL_WIDTH, containerHeight / REF_VIRTUAL_HEIGHT);
    const scale = Math.max(0.85, Math.min(1.5, rawScale));

    const virtualWidth = containerWidth / scale;
    const virtualHeight = containerHeight / scale;

    cvs.width = Math.round(containerWidth * dpr);
    cvs.height = Math.round(containerHeight * dpr);

    scaleRef.current = scale;
    offRef.current = { x: (virtualWidth - VW) / 2, y: (virtualHeight - VH) / 2 };
    vwRef.current = virtualWidth;
    vhRef.current = virtualHeight;
  }, []);

  // ── Setup hole ─────────────────────────────────────────
  const setupHole = useCallback((idx) => {
    const h = holesRef.current[idx];
    if (!h) return;
    ball.current = { x: h.ball.x, y: h.ball.y, vx: 0, vy: 0 };
    lastShotPos.current = { x: h.ball.x, y: h.ball.y };
    moving.current = false;
    strokes.current = 0;
    sinkAnim.current = { active: false, t: 0 };
    fallAnim.current = { active: false, t: 0, x: 0, y: 0 };
    // (msgAnim removed)
    fadeAnim.current = { active: false, t: 0 };
    ballSunk.current = false;
    aiming.current = false;
    holeTimerRef.current = holeTimeLimitRef.current;
    holeTimerRunning.current = gameActive.current; // Don't start until countdown done
    holeElapsedRef.current = 0;

  }, []);

  // ── Shoot ball ─────────────────────────────────────────
  const shoot = useCallback((dirX, dirY, power) => {
    if (moving.current || done.current || sinkAnim.current.active || fallAnim.current.active) return;
    const b = ball.current;
    lastShotPos.current = { x: b.x, y: b.y };
    b.vx = dirX * power * MAX_POWER;
    b.vy = dirY * power * MAX_POWER;
    moving.current = true;
    strokes.current += 1;
    beep(220, 0.08, 0.1);
    vibrate(15);
  }, [beep]);

  // ── Particle burst (matches runner/sidescroller pattern) ─
  const spawnParticles = useCallback((x, y, count = 8) => {
    const c = col.current;
    const sparkleColors = [c.ball, c.surface, c.flag, c.ball];
    for (let i = 0; i < count; i++) {
      const angle = (Math.PI * 2 * i) / count + (Math.random() - 0.5) * 0.4;
      const speed = 1.5 + Math.random() * 2.5;
      const isStar = count > 10 && Math.random() > 0.5;
      particlesRef.current.push({
        x, y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 1.8,
        size: isStar ? 2 + Math.random() * 2 : 1.2 + Math.random() * 1.5,
        opacity: 1.0,
        life: 0,
        color: sparkleColors[Math.floor(Math.random() * sparkleColors.length)],
        shape: isStar ? 'star' : 'circle',
        rotation: Math.random() * Math.PI * 2,
        rotSpeed: (Math.random() - 0.5) * 6,
      });
    }
  }, []);

  // ── Physics update ─────────────────────────────────────
  // Uses substep integration to prevent tunneling: the ball never moves
  // more than half its radius per substep, so it can't skip through walls
  // even at max power. Only fast frames need multiple substeps; slow
  // rolling uses a single step — zero overhead when it's not needed.
  const updatePhysics = useCallback(() => {
    if (!moving.current) return;
    const b = ball.current;
    const dt60 = dt60Ref.current;
    const h = holesRef.current[holeIdx.current];

    // Accumulate roll angle based on distance travelled
    const speed0 = Math.sqrt(b.vx * b.vx + b.vy * b.vy);
    ballRollAngle.current += (speed0 * dt60) / BALL_R;

    // Determine substep count based on current speed
    const maxStepDist = BALL_R * 0.5; // half-radius per substep
    const numSteps = Math.max(1, Math.ceil(speed0 * dt60 / maxStepDist));
    const subDt = dt60 / numSteps;
    let bouncedObstacle = false; // track for sound (play once per frame)
    let bouncedWall = false;

    for (let si = 0; si < numSteps; si++) {
      // ── Move ──
      b.x += b.vx * subDt;
      b.y += b.vy * subDt;

      // ── Friction ──
      const spd = Math.sqrt(b.vx * b.vx + b.vy * b.vy);
      const fric = spd < 1.5 ? FRICTION * 0.96 : FRICTION;
      b.vx *= Math.pow(fric, subDt);
      b.vy *= Math.pow(fric, subDt);

      // ── Obstacle collisions ──
      if (h) {
        let obstacleHit = false;
        for (const w of h.obstacles) {
          const hit = circleRect(b.x, b.y, BALL_R - 2, w.x, w.y, w.w, w.h);
          if (hit) {
            obstacleHit = true;
            bouncedObstacle = true;
            b.x += hit.nx * hit.overlap;
            b.y += hit.ny * hit.overlap;
            const dot = b.vx * hit.nx + b.vy * hit.ny;
            b.vx = (b.vx - 2 * dot * hit.nx) * BOUNCE_LOSS;
            b.vy = (b.vy - 2 * dot * hit.ny) * BOUNCE_LOSS;
            // Guarantee minimum bounce-away speed so ball never freezes on contact
            const postSpd = Math.sqrt(b.vx * b.vx + b.vy * b.vy);
            if (postSpd < 1.5) {
              const scale = 1.5 / (postSpd || 0.001);
              b.vx = (b.vx || hit.nx) * scale;
              b.vy = (b.vy || hit.ny) * scale;
            }
          }
        }
        // Only pull back if an obstacle collision actually pushed ball outside
        if (obstacleHit && h.courseShape && !pointInCourseShape(b.x, b.y, h.courseShape)) {
          const cx = h.ball.x, cy = h.ball.y;
          const dx = cx - b.x, dy = cy - b.y;
          const d = Math.sqrt(dx * dx + dy * dy) || 1;
          for (let step = 0; step < 5; step++) {
            b.x += (dx / d) * (BALL_R + 2);
            b.y += (dy / d) * (BALL_R + 2);
            if (pointInCourseShape(b.x, b.y, h.courseShape)) break;
          }
        }
      }

      // ── Void zones ──
      if (h?.voids) {
        let hitVoid = false;
        for (const v of h.voids) {
          if (b.x >= v.x && b.x <= v.x + v.w &&
              b.y >= v.y && b.y <= v.y + v.h) {
            b.x = h.ball.x; b.y = h.ball.y;
            b.vx = 0; b.vy = 0;
            moving.current = false;
            if (audioPoolRef.current?._urls?.death) {
              playSound(audioPoolRef.current, 'death');
            } else { beep(120, 0.3, 0.12); }
            vibrate(30);
            hitVoid = true;
            break;
          }
        }
        if (hitVoid) return;
      }

      // ── Boundary collisions (polygon edges or play area edges) ──
      if (h?.courseShape) {
        const cs = h.courseShape;
        const polys = Array.isArray(cs[0]) ? cs : [cs];
        const openEdges = h.openEdges || [];
        // Build set of vertices that border open edges — exclude from collision
        const openVerts = new Set();
        for (const oe of openEdges) {
          openVerts.add(oe);
          openVerts.add((oe + 1) % (Array.isArray(cs[0]) ? cs[0].length : cs.length));
        }
        // Collect all wall hits this substep, then resolve once to avoid
        // double-reflection at corners killing the ball's velocity.
        const wallHits = [];
        for (let pi = 0; pi < polys.length; pi++) {
          const poly = polys[pi];
          const n = poly.length;
          const isOuter = pi === 0;
          for (let i = 0; i < n; i++) {
            if (isOuter && openEdges.includes(i)) continue;
            const j = (i + 1) % n;
            const segDx = poly[j].x - poly[i].x;
            const segDy = poly[j].y - poly[i].y;
            const segLen2 = segDx * segDx + segDy * segDy;
            const segLen = Math.sqrt(segLen2);
            const trim = (isOuter && segLen > 0) ? (BALL_R * 3) / segLen : 0;
            let tMin = (isOuter && openVerts.has(i)) ? trim : 0;
            let tMax = (isOuter && openVerts.has(j)) ? 1 - trim : 1;
            if ((tMin > 0 || tMax < 1) && segLen2 > 0) {
              const rawT = ((b.x - poly[i].x) * segDx + (b.y - poly[i].y) * segDy) / segLen2;
              if ((tMin > 0 && rawT < tMin) || (tMax < 1 && rawT > tMax)) {
                if (!pointInPoly(b.x, b.y, poly)) continue;
                tMin = 0;
                tMax = 1;
              }
            }
            const hit = circleSegment(b.x, b.y, BALL_R, poly[i].x, poly[i].y, poly[j].x, poly[j].y, tMin, tMax);
            if (hit) wallHits.push(hit);
          }
        }
        if (wallHits.length > 0) {
          bouncedWall = true;
          // Push ball out of ALL overlapping walls
          for (const hit of wallHits) {
            b.x += hit.nx * hit.overlap;
            b.y += hit.ny * hit.overlap;
          }
          // Reflect velocity once using averaged normal (handles corners cleanly)
          let anx = 0, any = 0;
          for (const hit of wallHits) { anx += hit.nx; any += hit.ny; }
          const alen = Math.sqrt(anx * anx + any * any) || 1;
          anx /= alen; any /= alen;
          const dot = b.vx * anx + b.vy * any;
          if (dot < 0) { // only reflect if moving into the wall
            b.vx = (b.vx - 2 * dot * anx) * BOUNCE_LOSS;
            b.vy = (b.vy - 2 * dot * any) * BOUNCE_LOSS;
          }
          // Minimum bounce-away speed
          const wSpd = Math.sqrt(b.vx * b.vx + b.vy * b.vy);
          if (wSpd < 1.0) {
            const ws = 1.0 / (wSpd || 0.001);
            b.vx = (b.vx || anx) * ws;
            b.vy = (b.vy || any) * ws;
          }
        }
        // Out-of-bounds fallback
        if (!fallAnim.current.active && !pointInCourseShape(b.x, b.y, cs)) {
          const fbPolys = Array.isArray(cs[0]) ? cs : [cs];
          if (!pointInPoly(b.x, b.y, fbPolys[0])) {
            // Ball is outside the outer polygon — legitimate fall
            fallAnim.current = { active: true, t: 0, x: b.x, y: b.y, vx: b.vx * 0.7, vy: b.vy * 0.7, fallZ: 0, fallVZ: 0 };
            flashAnim.current = { active: true, t: 0, color: 'danger' };
            shakeRef.current.amount = 5;
            b.vx = 0; b.vy = 0;
            moving.current = false;
            if (audioPoolRef.current?._urls?.death) {
              playSound(audioPoolRef.current, 'death');
            } else { beep(120, 0.3, 0.12); }
            vibrate(30);
            return;
          } else {
            // Ball tunnelled into a cutout island — push it back out
            const cx = h.ball.x, cy = h.ball.y;
            const dx = cx - b.x, dy = cy - b.y;
            const d = Math.sqrt(dx * dx + dy * dy) || 1;
            for (let step = 0; step < 8; step++) {
              b.x += (dx / d) * (BALL_R + 2);
              b.y += (dy / d) * (BALL_R + 2);
              if (pointInCourseShape(b.x, b.y, h.courseShape)) break;
            }
            b.vx *= -0.3; b.vy *= -0.3; // reverse + dampen
          }
        }
      } else {
        if (b.x - BALL_R < PA.x) { b.x = PA.x + BALL_R; b.vx = -b.vx * BOUNCE_LOSS; }
        if (b.x + BALL_R > PA.x + PA.w) { b.x = PA.x + PA.w - BALL_R; b.vx = -b.vx * BOUNCE_LOSS; }
        if (b.y - BALL_R < PA.y) { b.y = PA.y + BALL_R; b.vy = -b.vy * BOUNCE_LOSS; }
        if (b.y + BALL_R > PA.y + PA.h) { b.y = PA.y + PA.h - BALL_R; b.vy = -b.vy * BOUNCE_LOSS; }
      }

      // ── Early exit if ball stopped during substep ──
      if (!moving.current) return;
    } // end substep loop

    // Play bounce sounds once per frame (not per substep)
    if (bouncedObstacle || bouncedWall) {
      if (audioPoolRef.current?._urls?.bounce) {
        playSound(audioPoolRef.current, 'bounce');
      } else {
        beep(bouncedObstacle ? 180 : 220, 0.05, 0.08);
      }
      if (speed0 > 6) shakeRef.current.amount = Math.min(speed0 * 0.025, 0.3);
    }

    // ── Shared hole-sink handler ──
    const sinkBall = (holeData) => {
      moving.current = false;
      b.vx = 0; b.vy = 0;
      sinkAnim.current = { active: true, t: 0 };
      holeTimerRunning.current = false;
      const pts = calcHolePoints(strokes.current, holeElapsedRef.current);
      totalScore.current += pts;
      holeScores.current.push({ strokes: strokes.current, points: pts });
      if (onScoreChange) onScoreChange(totalScore.current);

      const label = `+${pts}`;
      sinkAnim.current.label = label;
      if (floatingTextsRef.current.length >= 3) floatingTextsRef.current.shift();
      floatingTextsRef.current.push({
        x: holeData.hole.x, y: holeData.hole.y - 30,
        text: label, life: 1.0, velocity: -0.7, points: pts,
      });
      if (audioPoolRef.current?._urls?.collect) {
        playSound(audioPoolRef.current, 'collect');
      } else {
        beep(523, 0.15, 0.2);
        setTimeout(() => beep(659, 0.15, 0.2), 100);
        setTimeout(() => beep(784, 0.2, 0.2), 200);
      }
      if (strokes.current === 1) {
        spawnParticles(holeData.hole.x, holeData.hole.y, 32);
        flashAnim.current = { active: true, t: 0, color: 'warm' };
        shakeRef.current.amount = 3;
        vibrate([20, 40, 20, 40, 20]);
      } else if (pts >= 700) {
        spawnParticles(holeData.hole.x, holeData.hole.y, 16);
        vibrate([15, 30, 15]);
      } else {
        spawnParticles(holeData.hole.x, holeData.hole.y, 6);
        vibrate([10, 30, 10]);
      }
    };

    // Hole detection — graduated capture: closer to centre = tolerates higher speed
    if (h) {
      const dx = b.x - h.hole.x;
      const dy = b.y - h.hole.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const speed = Math.sqrt(b.vx * b.vx + b.vy * b.vy);
      const closeness = 1 - Math.min(dist / HOLE_R, 1);
      const captureSpeed = HOLE_CAPTURE_SPEED * (1 + closeness * 2);
      if (dist < HOLE_R && speed < captureSpeed) {
        sinkBall(h);
        return;
      }
    }

    // Stop check
    const speed = Math.sqrt(b.vx * b.vx + b.vy * b.vy);
    if (speed < STOP_V) {
      b.vx = 0;
      b.vy = 0;
      moving.current = false;

      // Post-stop hole capture — generous radius for a stopped ball
      // If the ball visually overlaps the hole at all, it drops in
      if (h) {
        const sdx = b.x - h.hole.x;
        const sdy = b.y - h.hole.y;
        const sDist = Math.sqrt(sdx * sdx + sdy * sdy);
        if (sDist < HOLE_R + BALL_R * 0.7) {
          sinkBall(h);
          return;
        }
      }

      // Max strokes reached?
      if (strokes.current >= MAX_STROKES) {
        holeTimerRunning.current = false;
        const pts3 = calcHolePoints(MAX_STROKES, holeElapsedRef.current);
        totalScore.current += pts3;
        holeScores.current.push({ strokes: MAX_STROKES, points: pts3 });
        if (onScoreChange) onScoreChange(totalScore.current);
  
        if (audioPoolRef.current?._urls?.death) {
          playSound(audioPoolRef.current, 'death');
        } else { beep(120, 0.3, 0.12); }
        vibrate(30);
        const label3 = `+${pts3}`;
        // Go straight to next hole or finish
        const nextIdx3 = holeIdx.current + 1;
        if (nextIdx3 >= holesRef.current.length) {
          done.current = true;
          if (onComplete) onComplete(totalScore.current, { holeScores: holeScores.current });
          return;
        }
        fadeAnim.current = { active: true, t: 0, _nextIdx: nextIdx3 };
        if (floatingTextsRef.current.length >= 3) floatingTextsRef.current.shift();
        floatingTextsRef.current.push({
          x: ball.current.x, y: ball.current.y - 30,
          text: label3, life: 1.0, velocity: -0.7,
          points: pts3,
        });
      }
    }
  }, [beep, onScoreChange]);

  // ── Drawing ────────────────────────────────────────────
  const draw = useCallback(() => {
    const ctx = ctxRef.current;
    const cvs = canvasRef.current;
    if (!ctx || !cvs) return;
    const dpr = dprRef.current;
    const s = scaleRef.current;
    const o = offRef.current;

    // Defensive reset — if a previous frame's draw() crashed mid-render,
    // stale transforms/clips/alpha persist on the save stack. Reset fully.
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
    ctx.clearRect(0, 0, cvs.width, cvs.height);

    // ── Background (always drawn, visible behind countdown overlay) ──
    const bgImg = bgImgRef.current;
    const vw = vwRef.current;
    const vh = vhRef.current;
    ctx.save();
    ctx.scale(dpr * s, dpr * s);
    if (isProceduralBackground(settings?.backgroundImage || settings?.gameBackgroundImage)) {
      // Skip — ProceduralBackground canvas renders behind this transparent canvas
    } else if (bgImg) {
      const iw = bgImg.naturalWidth || bgImg.width;
      const ih = bgImg.naturalHeight || bgImg.height;
      const canvasAspect = vw / vh;
      const imgAspect = iw / ih;
      let sx = 0, sy = 0, sw = iw, sh = ih;
      if (imgAspect > canvasAspect) {
        sw = ih * canvasAspect; sx = (iw - sw) / 2;
      } else {
        sh = iw / canvasAspect; sy = (ih - sh) / 2;
      }
      ctx.drawImage(bgImg, sx, sy, sw, sh, 0, 0, vw, vh);
    } else {
      // ── Sky gradient background ──
      const skyGrad = ctx.createLinearGradient(0, 0, 0, vh);
      skyGrad.addColorStop(0, '#6BB8D6');
      skyGrad.addColorStop(0.35, '#8ECAE6');
      skyGrad.addColorStop(0.65, '#B4DCF0');
      skyGrad.addColorStop(1, '#D6EDF8');
      ctx.fillStyle = skyGrad;
      ctx.fillRect(0, 0, vw, vh);

      // ── Procedural clouds (generated once per resize, animated drift) ──
      if (!cloudsRef.current || cloudsRef.current._w !== vw || cloudsRef.current._h !== vh) {
        const clouds = [];
        const count = 10;
        for (let i = 0; i < count; i++) {
          const cx = Math.random() * vw * 1.4 - vw * 0.2;
          const cy = Math.random() * vh;
          const sc = 0.5 + Math.random() * 1.5;
          const opacity = 0.35 + Math.random() * 0.4;
          // Each cloud = 3-6 overlapping circles
          const blobCount = 3 + Math.floor(Math.random() * 4);
          const blobs = [];
          for (let j = 0; j < blobCount; j++) {
            blobs.push({
              ox: (Math.random() - 0.3) * 50 * sc,
              oy: (Math.random() - 0.5) * 20 * sc,
              r: (15 + Math.random() * 25) * sc,
            });
          }
          clouds.push({ x: cx, y: cy, scale: sc, opacity, blobs, speed: 0.08 + Math.random() * 0.12, layer: i < 3 ? 'front' : 'back' });
        }
        clouds._w = vw;
        clouds._h = vh;
        cloudsRef.current = clouds;
      }

      // Draw background clouds (behind course)
      const time = frameTimeRef.current;
      for (const cloud of cloudsRef.current) {
        if (cloud.layer === 'front') continue;
        ctx.globalAlpha = cloud.opacity;
        ctx.fillStyle = '#fff';
        const drift = (time * cloud.speed * 15) % (vw + 200);
        const cx = ((cloud.x + drift + vw * 0.2) % (vw + 200)) - 100;
        ctx.beginPath();
        for (const blob of cloud.blobs) {
          ctx.moveTo(cx + blob.ox + blob.r, cloud.y + blob.oy);
          ctx.arc(cx + blob.ox, cloud.y + blob.oy, blob.r, 0, Math.PI * 2);
        }
        ctx.fill();
      }
      ctx.globalAlpha = 1;
    }
    ctx.restore();

    // Don't draw course/game content during countdown
    const c = col.current;
    const h = holesRef.current[holeIdx.current];
    if (!gameActive.current || !h) return;

    // Switch to virtual coordinate system for game elements
    ctx.save();
    ctx.scale(dpr * s, dpr * s);
    const sk = shakeRef.current;
    ctx.translate(o.x + sk.x, o.y + sk.y);

    // ── 2.5D Floating Island Course ────────────────────────
    const shape = h.courseShape;
    const oe = h.openEdges || [];

    // Build polygon list (supports compound shapes)
    const coursePolys = shape
      ? (Array.isArray(shape[0]) ? shape : [shape])
      : [[ { x: PA.x, y: PA.y }, { x: PA.x + PA.w, y: PA.y },
           { x: PA.x + PA.w, y: PA.y + PA.h }, { x: PA.x, y: PA.y + PA.h } ]];
    const outerPoly = coursePolys[0];
    const polyN = outerPoly.length;

    // ── Drop shadow (offset below island for floating effect) ──
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.25)';
    ctx.shadowBlur = 24;
    ctx.shadowOffsetX = 4;
    ctx.shadowOffsetY = DEPTH + 8;
    ctx.fillStyle = 'rgba(0,0,0,0.01)'; // Near-invisible fill to trigger shadow
    ctx.beginPath();
    coursePath(ctx, shape || coursePolys[0]);
    ctx.fill('evenodd');
    ctx.restore();

    // ── Depth side walls (drawn behind top surface) ──
    // All wall quads share the same offset vector (DEPTH_DX, DEPTH) so
    // at every corner the quads share their offset vertex → seamless joins.
    const DEPTH_DX = 5; // Slight rightward lean for isometric feel
    const sideBottom = darken(c.course, SIDE_DARKEN);
    const sideRight = darken(c.course, SIDE_DARKEN_LIGHT);

    // Collect wall quads for ALL closed edges, sorted back-to-front.
    // No normal-based filtering — walls are drawn for every edge.
    // The top surface (drawn after) naturally covers walls that face
    // inward/upward. For concave shapes (E, L, etc.) inner shelf
    // edges need walls too — the gap between shelves reveals them.
    const wallQuads = [];
    for (let i = 0; i < polyN; i++) {
      const A = outerPoly[i];
      const B = outerPoly[(i + 1) % polyN];
      const edx = B.x - A.x, edy = B.y - A.y;
      const edLen = Math.sqrt(edx * edx + edy * edy);
      if (edLen < 1) continue;
      // Colour by edge orientation: horizontal = bottom shade, vertical = right shade
      const isHorizontal = Math.abs(edx) > Math.abs(edy);
      const wallColor = isHorizontal ? sideBottom : sideRight;
      wallQuads.push({ A, B, color: wallColor, sortY: Math.min(A.y, B.y) });
    }
    wallQuads.sort((a, b) => a.sortY - b.sortY);

    for (const q of wallQuads) {
      ctx.fillStyle = q.color;
      ctx.beginPath();
      ctx.moveTo(q.A.x, q.A.y);
      ctx.lineTo(q.B.x, q.B.y);
      ctx.lineTo(q.B.x + DEPTH_DX, q.B.y + DEPTH);
      ctx.lineTo(q.A.x + DEPTH_DX, q.A.y + DEPTH);
      ctx.closePath();
      ctx.fill();
    }
    // Subtle bottom edge line for ground definition
    ctx.strokeStyle = darken(c.course, SIDE_DARKEN + 0.12);
    ctx.lineWidth = 1;
    for (const q of wallQuads) {
      ctx.beginPath();
      ctx.moveTo(q.A.x + DEPTH_DX, q.A.y + DEPTH);
      ctx.lineTo(q.B.x + DEPTH_DX, q.B.y + DEPTH);
      ctx.stroke();
    }

    // ── Top surface fill ──
    ctx.fillStyle = c.course;
    ctx.beginPath();
    coursePath(ctx, shape || coursePolys[0]);
    ctx.fill('evenodd');

    // ── Turf texture (clipped to top surface) ──
    ctx.save();
    ctx.beginPath();
    coursePath(ctx, shape || coursePolys[0]);
    ctx.clip('evenodd');
    drawTurf(ctx, c.course, floorStyleRef.current, PA);
    ctx.restore();

    // ── Raised 3D bumper rails on solid walls ───────────
    // Cream/white bumper rails like real mini golf courses.
    // Their strong presence on solid walls makes the ABSENCE on
    // open edges immediately obvious (sandy strip + no bumper).
    {
      const BUMPER_W = 5;
      ctx.save();
      // Clip to course interior so stroke only extends inward
      ctx.beginPath();
      coursePath(ctx, shape || coursePolys[0]);
      ctx.clip('evenodd');

      ctx.lineJoin = 'miter';
      ctx.lineCap = 'butt';

      // Layer 1 (widest): shadow where bumper meets green
      const bumperColor = c.border;
      ctx.lineWidth = BUMPER_W * 2;
      ctx.strokeStyle = darken(bumperColor, 0.25);
      ctx.beginPath();
      courseStrokePath(ctx, shape || coursePolys[0], oe);
      ctx.stroke();

      // Layer 2: bumper body
      ctx.lineWidth = BUMPER_W * 2 - 3;
      ctx.strokeStyle = bumperColor;
      ctx.beginPath();
      courseStrokePath(ctx, shape || coursePolys[0], oe);
      ctx.stroke();

      // Layer 3 (narrowest): bright highlight
      ctx.lineWidth = BUMPER_W * 2 - 7;
      ctx.strokeStyle = lighten(bumperColor, 0.15);
      ctx.beginPath();
      courseStrokePath(ctx, shape || coursePolys[0], oe);
      ctx.stroke();

      ctx.restore();

      // Outer definition line along the boundary (drawn unclipped)
      ctx.lineJoin = 'miter';
      ctx.lineCap = 'butt';
      ctx.lineWidth = 1.5;
      ctx.strokeStyle = darken(bumperColor, 0.35);
      ctx.beginPath();
      courseStrokePath(ctx, shape || coursePolys[0], oe);
      ctx.stroke();
    }


    // ── Void zones (sky-through gaps in the island) ────────
    if (h.voids) {
      ctx.save();
      ctx.beginPath();
      if (shape) { coursePath(ctx, shape); ctx.clip('evenodd'); } else { roundRect(ctx, PA.x, PA.y, PA.w, PA.h, 12); ctx.clip(); }
      for (const v of h.voids) {
        // Sky visible through the gap
        const voidGrad = ctx.createLinearGradient(v.x, v.y, v.x, v.y + v.h);
        voidGrad.addColorStop(0, '#8ECAE6');
        voidGrad.addColorStop(1, '#B4DCF0');
        ctx.fillStyle = voidGrad;
        ctx.fillRect(v.x, v.y, v.w, v.h);
        // Inward shadow edges — looking down through the island
        const ins = 5;
        const edgeShadow = ctx.createLinearGradient(v.x, v.y, v.x, v.y + ins);
        edgeShadow.addColorStop(0, 'rgba(0,0,0,0.35)');
        edgeShadow.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = edgeShadow;
        ctx.fillRect(v.x, v.y, v.w, ins);
        const edgeShadowL = ctx.createLinearGradient(v.x, v.y, v.x + ins, v.y);
        edgeShadowL.addColorStop(0, 'rgba(0,0,0,0.25)');
        edgeShadowL.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = edgeShadowL;
        ctx.fillRect(v.x, v.y, ins, v.h);
      }
      ctx.restore();
    }

    // ── Hole (sunken with depth gradient) ─────────────────
    const hx = h.hole.x, hy = h.hole.y;
    // Outer rim — slightly raised lip around the hole
    ctx.beginPath();
    ctx.arc(hx, hy, HOLE_R + 4, 0, Math.PI * 2);
    ctx.fillStyle = darken(c.course, 0.12);
    ctx.fill();
    // Rim highlight (top edge catch)
    ctx.beginPath();
    ctx.arc(hx, hy, HOLE_R + 4, 0, Math.PI * 2);
    ctx.strokeStyle = lighten(c.course, 0.08);
    ctx.lineWidth = 1;
    ctx.stroke();
    // Inner rim
    ctx.beginPath();
    ctx.arc(hx, hy, HOLE_R + 1, 0, Math.PI * 2);
    ctx.fillStyle = darken(c.course, 0.3);
    ctx.fill();
    // Hole with depth gradient — dark center fading to rim
    const holeGrad = ctx.createRadialGradient(hx, hy - 2, 0, hx, hy, HOLE_R);
    holeGrad.addColorStop(0, '#050505');
    holeGrad.addColorStop(0.7, '#111');
    holeGrad.addColorStop(1, '#2a2a2a');
    ctx.beginPath();
    ctx.arc(hx, hy, HOLE_R, 0, Math.PI * 2);
    ctx.fillStyle = holeGrad;
    ctx.fill();
    // Subtle inner shadow crescent (top-lit depth illusion)
    const crescGrad = ctx.createRadialGradient(hx, hy + 3, HOLE_R * 0.3, hx, hy, HOLE_R);
    crescGrad.addColorStop(0, 'rgba(0,0,0,0)');
    crescGrad.addColorStop(0.6, 'rgba(0,0,0,0)');
    crescGrad.addColorStop(1, 'rgba(0,0,0,0.3)');
    ctx.beginPath();
    ctx.arc(hx, hy, HOLE_R, 0, Math.PI * 2);
    ctx.fillStyle = crescGrad;
    ctx.fill();
    // Pulsing target ring — draws eye to the hole
    const pulse = 0.5 + 0.5 * Math.sin(frameTimeRef.current * 3);
    ctx.beginPath();
    ctx.arc(hx, hy, HOLE_R + 8 + pulse * 3, 0, Math.PI * 2);
    ctx.strokeStyle = `rgba(255, 255, 255, ${0.08 + pulse * 0.12})`;
    ctx.lineWidth = 1.5;
    ctx.stroke();

    // ── Obstacles ─────────────────────────────────────────
    // Grounded with an ambient-occlusion halo that darkens the green around
    // the base, plus a thick dark inset outline so the obstacle reads as
    // pressed into the surface rather than floating above it. No drop
    // shadow — that's the idiom that was making it look detached.
    const obsImg = obstacleImgRef.current;
    const OBS_R = 7;
    for (const ob of h.obstacles) {
      const ow = ob.w;
      const oh = ob.h;

      // 1. Ambient occlusion halo — radial-style darkening on the green just
      //    around the obstacle. Draw a slightly larger rounded rect with a
      //    soft blur underneath the block.
      ctx.save();
      ctx.shadowColor = 'rgba(0,0,0,0.32)';
      ctx.shadowBlur = 10;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;
      ctx.fillStyle = 'rgba(0,0,0,0.4)';
      ctx.beginPath();
      roundRect(ctx, ob.x - 1, ob.y - 1, ow + 2, oh + 2, OBS_R + 1);
      ctx.fill();
      ctx.restore();

      // 2. Top face — solid colour or custom image
      ctx.fillStyle = c.obstacle;
      ctx.beginPath();
      roundRect(ctx, ob.x, ob.y, ow, oh, OBS_R);
      ctx.fill();

      if (obsImg) {
        ctx.save();
        ctx.beginPath();
        roundRect(ctx, ob.x + 0.5, ob.y + 0.5, ow - 1, oh - 1, OBS_R);
        ctx.clip();
        const iw = obsImg.naturalWidth || obsImg.width;
        const ih = obsImg.naturalHeight || obsImg.height;
        const scale = Math.min(ow / iw, oh / ih);
        const dw = iw * scale;
        const dh = ih * scale;
        ctx.drawImage(obsImg, ob.x + (ow - dw) / 2, ob.y + (oh - dh) / 2, dw, dh);
        ctx.restore();
      } else {
        // Subtle inner gradient bevel — slight lift at top, weight at bottom
        ctx.save();
        ctx.beginPath();
        roundRect(ctx, ob.x + 0.5, ob.y + 0.5, ow - 1, oh - 1, OBS_R);
        ctx.clip();
        const grad = ctx.createLinearGradient(0, ob.y, 0, ob.y + oh);
        grad.addColorStop(0, 'rgba(255,255,255,0.15)');
        grad.addColorStop(0.4, 'rgba(255,255,255,0)');
        grad.addColorStop(0.7, 'rgba(0,0,0,0)');
        grad.addColorStop(1, 'rgba(0,0,0,0.22)');
        ctx.fillStyle = grad;
        ctx.fillRect(ob.x, ob.y, ow, oh);
        ctx.restore();
      }

      // 3. Inset dark outline — sells the "pressed into surface" feel
      ctx.strokeStyle = darken(c.obstacle, 0.55);
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      roundRect(ctx, ob.x + 0.75, ob.y + 0.75, ow - 1.5, oh - 1.5, OBS_R - 0.5);
      ctx.stroke();
    }

    // ── Ball state (computed before draw for z-ordering) ─
    const b = ball.current;
    let ballScale = ballSunk.current ? 0 : 1;
    let ballAlpha = ballSunk.current ? 0 : 1;
    let ballDrawX = b.x;
    let ballDrawY = b.y;
    let ballSpin = ballRollAngle.current;
    if (sinkAnim.current.active) {
      const t = sinkAnim.current.t;
      // Phase 1 (0–0.5): gentle arc into hole centre
      // Phase 2 (0.5–1): shrink into hole
      if (t < 0.5) {
        const p = t / 0.5;
        const ease = p * p * (3 - 2 * p); // smooth ease-in-out
        const startR = Math.max(6, Math.hypot(b.x - h.hole.x, b.y - h.hole.y));
        const spiralR = (1 - ease) * startR;
        const startAngle = sinkAnim.current.startAngle ??
          (sinkAnim.current.startAngle = Math.atan2(b.y - h.hole.y, b.x - h.hole.x));
        const spiralAngle = startAngle + ease * Math.PI * 1.2;
        ballDrawX = h.hole.x + Math.cos(spiralAngle) * spiralR;
        ballDrawY = h.hole.y + Math.sin(spiralAngle) * spiralR;
        ballScale = 1 - ease * 0.1;
        ballAlpha = 1;
        ballSpin = ease * Math.PI * 1.5;
      } else {
        const p = (t - 0.5) / 0.5;
        const ease = p * p;
        ballDrawX = h.hole.x;
        ballDrawY = h.hole.y;
        ballScale = Math.max(0, 0.9 * (1 - ease));
        ballAlpha = Math.max(0, 1 - ease);
        ballSpin = Math.PI * 1.5 + p * Math.PI;
      }
    }
    // Fall-off animation — perspective shrink + vertical offset from gravity
    if (fallAnim.current.active) {
      const fa = fallAnim.current;
      ballDrawX = fa.x;
      // Ball drops below the island surface — offset Y by fall depth
      // (scaled down so it looks like it's falling away, not sliding down)
      ballDrawY = fa.y + (fa.fallZ || 0) * 0.3;
      // Perspective: shrink as fall depth increases (further from camera)
      const perspScale = Math.max(0, 1 - (fa.fallZ || 0) / 200);
      ballScale = perspScale;
      // Fade out in the last stretch so it doesn't pop
      ballAlpha = Math.max(0, perspScale < 0.3 ? perspScale / 0.3 : 1);
      // Tumble — rotation speed increases with fall velocity
      ballSpin = (fa.fallZ || 0) * 0.08;
    }


    // ── Flag & Ball (z-ordered: higher Y = closer to viewer = drawn later) ──
    const drawFlag = () => {
      const fx = h.hole.x;
      const fy = h.hole.y;
      const poleH = 86;
      const poleTop = fy - poleH;
      // Pole — static, no animation
      ctx.strokeStyle = '#ccc';
      ctx.lineWidth = 2.5;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(fx, fy + 2);
      ctx.lineTo(fx, poleTop);
      ctx.stroke();
      ctx.lineCap = 'butt';

      const fImg = flagImgRef.current;
      if (fImg) {
        const maxW = 72;
        const maxH = 60;
        const iw = fImg.naturalWidth || fImg.width;
        const ih = fImg.naturalHeight || fImg.height;
        const scale = Math.min(maxW / iw, maxH / ih);
        const sw = iw * scale;
        const sh = ih * scale;
        const swing = Math.sin(frameTimeRef.current * 2) * 0.05;
        const sx = -sw / 2;
        const sy = 2;
        ctx.save();
        ctx.translate(fx, poleTop);
        ctx.rotate(swing);
        // Crisp image rendering
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        // Drop shadow
        ctx.shadowColor = 'rgba(0,0,0,0.3)';
        ctx.shadowBlur = 6;
        ctx.shadowOffsetX = 1;
        ctx.shadowOffsetY = 3;
        ctx.drawImage(fImg, sx, sy, sw, sh);
        // Pole bracket — small circle where pole meets flag
        ctx.shadowColor = 'transparent';
        ctx.beginPath();
        ctx.arc(0, sy, 3.5, 0, Math.PI * 2);
        ctx.fillStyle = '#bbb';
        ctx.fill();
        ctx.strokeStyle = '#999';
        ctx.lineWidth = 0.8;
        ctx.stroke();
        ctx.restore();
      } else {
        // Default triangle pennant
        const wave = Math.sin(frameTimeRef.current * 3) * 2;
        const flagTop = poleTop;
        const flagMid = poleTop + 10;
        const flagBot = poleTop + 20;
        ctx.save();
        ctx.shadowColor = 'rgba(0,0,0,0.15)';
        ctx.shadowBlur = 3;
        ctx.shadowOffsetY = 1;
        ctx.beginPath();
        ctx.moveTo(fx, flagTop);
        ctx.lineTo(fx + 22 + wave, flagMid);
        ctx.lineTo(fx, flagBot);
        ctx.closePath();
        ctx.fillStyle = c.flag;
        ctx.fill();
        ctx.restore();
        ctx.strokeStyle = darken(c.flag, 0.2);
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(fx, flagTop);
        ctx.lineTo(fx + 22 + wave, flagMid);
        ctx.lineTo(fx, flagBot);
        ctx.closePath();
        ctx.stroke();
      }
    };

    const drawBall = () => {
      if (ballAlpha <= 0 || ballScale < 0.05) return;
      const bx = ballDrawX;
      const by = ballDrawY;
      ctx.save();
      // During fall-off, clip ball to only render OUTSIDE the course surface
      // so it doesn't draw on top of other course geometry.
      // Delay until ball has fallen below the surface (fallZ > ball radius)
      // so the ball visibly rolls off the edge before being masked.
      if (fallAnim.current.active && shape && (fallAnim.current.fallZ || 0) > BALL_R) {
        ctx.beginPath();
        ctx.rect(-100, -100, VW + 200, VH + 200);
        coursePath(ctx, shape);
        ctx.clip('evenodd');
      }
      ctx.globalAlpha = ballAlpha;
      const bImg = ballImgRef.current;

      // ── Motion trail — fading ghosts behind the ball ──
      const spd = Math.sqrt(b.vx * b.vx + b.vy * b.vy);
      if (spd > 1.5 && moving.current && !sinkAnim.current.active && !fallAnim.current.active) {
        const trailCount = Math.min(4, Math.floor(spd / 2));
        const dr = bImg ? BALL_R * ballScale : BALL_R * 0.8 * ballScale;
        for (let i = trailCount; i >= 1; i--) {
          const t = i / (trailCount + 1);
          const tx = bx - b.vx * t * 0.6;
          const ty = by - b.vy * t * 0.6;
          const trailAlpha = (1 - t) * 0.15;
          ctx.globalAlpha = ballAlpha * trailAlpha;
          if (bImg) {
            const size = BALL_R * 2 * ballScale * (1 - t * 0.1);
            const bw = bImg.naturalWidth || bImg.width;
            const bh = bImg.naturalHeight || bImg.height;
            const bScale = Math.min(size / bw, size / bh);
            const dw = bw * bScale, dh = bh * bScale;
            ctx.drawImage(bImg, tx - dw / 2, ty - dh / 2, dw, dh);
          } else {
            ctx.beginPath();
            ctx.arc(tx, ty, dr * (1 - t * 0.15), 0, Math.PI * 2);
            ctx.fillStyle = c.ball;
            ctx.fill();
          }
        }
        ctx.globalAlpha = ballAlpha;
      }

      // Ground shadow — only when ball is on the course surface
      if (!fallAnim.current.active) {
        const shadowR = BALL_R * ballScale * 1.1;
        const shadowGrad = ctx.createRadialGradient(bx + 2, by + 3, 0, bx + 2, by + 3, shadowR);
        shadowGrad.addColorStop(0, 'rgba(0,0,0,0.25)');
        shadowGrad.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = shadowGrad;
        ctx.beginPath();
        ctx.arc(bx + 2, by + 3, shadowR, 0, Math.PI * 2);
        ctx.fill();
      }

      if (ballSpin) {
        ctx.translate(bx, by);
        ctx.rotate(ballSpin);
        ctx.translate(-bx, -by);
      }

      if (bImg) {
        const size = BALL_R * 2 * ballScale;
        const bw = bImg.naturalWidth || bImg.width;
        const bh = bImg.naturalHeight || bImg.height;
        const bScale = Math.min(size / bw, size / bh);
        const dw = bw * bScale, dh = bh * bScale;
        ctx.drawImage(bImg, bx - dw / 2, by - dh / 2, dw, dh);
      } else {
        const r = BALL_R * 0.8 * ballScale; // Slightly smaller default ball
        // Base circle
        ctx.beginPath();
        ctx.arc(bx, by, r, 0, Math.PI * 2);
        ctx.fillStyle = c.ball;
        ctx.fill();

        // Dimple pattern — subtle indentations
        ctx.save();
        ctx.beginPath();
        ctx.arc(bx, by, r, 0, Math.PI * 2);
        ctx.clip();
        const dimpleR = r * 0.14;
        const rings = [
          { count: 6, dist: r * 0.45, offset: 0 },
          { count: 8, dist: r * 0.75, offset: Math.PI / 8 },
        ];
        for (const ring of rings) {
          for (let i = 0; i < ring.count; i++) {
            const a = (Math.PI * 2 * i) / ring.count + ring.offset;
            const dx = Math.cos(a) * ring.dist;
            const dy = Math.sin(a) * ring.dist;
            ctx.beginPath();
            ctx.arc(bx + dx, by + dy, dimpleR, 0, Math.PI * 2);
            ctx.fillStyle = 'rgba(0,0,0,0.08)';
            ctx.fill();
          }
        }
        ctx.restore();

        // 3D shading gradient — light from top-left
        const grad = ctx.createRadialGradient(
          bx - r * 0.3, by - r * 0.3, r * 0.1,
          bx, by, r
        );
        grad.addColorStop(0, 'rgba(255,255,255,0.35)');
        grad.addColorStop(0.5, 'rgba(255,255,255,0)');
        grad.addColorStop(1, 'rgba(0,0,0,0.15)');
        ctx.beginPath();
        ctx.arc(bx, by, r, 0, Math.PI * 2);
        ctx.fillStyle = grad;
        ctx.fill();

        // Subtle edge ring
        ctx.beginPath();
        ctx.arc(bx, by, r - 0.5, 0, Math.PI * 2);
        ctx.strokeStyle = 'rgba(0,0,0,0.1)';
        ctx.lineWidth = 0.8;
        ctx.stroke();
      }
      ctx.restore();
    };

    // Ball behind flag (higher on screen) → draw ball first, flag on top
    if (b.y < h.hole.y) {
      drawBall();
      drawFlag();
    } else {
      drawFlag();
      drawBall();
    }

    // ── Aim line (dotted path style) ─────────────────────
    if (aiming.current && !moving.current && !sinkAnim.current.active && !fallAnim.current.active) {
      const as = aimStart.current;
      const ac = aimCur.current;
      const dx = ac.x - as.x;
      const dy = ac.y - as.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist > 5) {
        const power = Math.min(dist, MAX_DRAG) / MAX_DRAG;
        const ndx = -dx / dist;
        const ndy = -dy / dist;
        const lineLen = power * 300;

        ctx.save();
        // Dotted aim line — contrast against floor
        const aimOnLight = isLightColor(c.course);
        const dotColor = aimOnLight ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.8)';
        const dotR = 2.5;
        const MAX_AIM_LEN = 300;
        const totalLen = Math.min(lineLen, MAX_AIM_LEN);
        // Progressive spacing: each dot is further apart than the last
        const BASE_SPACING = 8;   // first gap
        const SPACING_GROW = 1.8; // each subsequent gap grows by this much
        // Pre-compute cumulative distances for each dot
        const dotThresholds = [];
        let cumDist = 0;
        for (let i = 0; cumDist < totalLen; i++) {
          cumDist += BASE_SPACING + i * SPACING_GROW;
          if (cumDist < totalLen) dotThresholds.push(cumDist);
        }
        const totalDots = dotThresholds.length;

        // ── Simulate ball path using real physics ──
        // Use the same collision radii as updatePhysics:
        //   obstacles: BALL_R - 2 (forgiving), walls: BALL_R (exact)
        const SIM_STEP = 2;
        const simRobs = BALL_R - 2; // obstacle collision radius
        const simRwall = BALL_R;    // wall collision radius
        let sx = b.x, sy = b.y;
        let svx = ndx, svy = ndy;
        let simDist = 0;
        let dotIdx = 0;
        let bounces = 0;
        const MAX_BOUNCES = 1;
        const pathDots = [];

        // Check if ball is currently touching any obstacle — use a small
        // clearance gap so the first sim step doesn't immediately collide
        let touchingObstacle = false;
        if (h) {
          for (const ob of h.obstacles) {
            if (circleRect(sx, sy, simRobs + 1, ob.x, ob.y, ob.w, ob.h)) {
              touchingObstacle = true;
              break;
            }
          }
        }

        const polys = h?.courseShape ? (Array.isArray(h.courseShape[0]) ? h.courseShape : [h.courseShape]) : null;
        const openEdgesArr = h?.openEdges || [];

        while (simDist < totalLen && dotIdx < totalDots && bounces <= MAX_BOUNCES) {
          // Step forward
          const stepLen = Math.min(SIM_STEP, totalLen - simDist);
          sx += svx * stepLen;
          sy += svy * stepLen;
          simDist += stepLen;

          // Obstacle collisions (circle vs AABB — BALL_R-2, same as physics)
          if (h && !touchingObstacle) {
            for (const ob of h.obstacles) {
              const hit = circleRect(sx, sy, simRobs, ob.x, ob.y, ob.w, ob.h);
              if (hit) {
                sx += hit.nx * hit.overlap;
                sy += hit.ny * hit.overlap;
                const dot2 = svx * hit.nx + svy * hit.ny;
                svx = svx - 2 * dot2 * hit.nx;
                svy = svy - 2 * dot2 * hit.ny;
                bounces++;
                if (bounces > MAX_BOUNCES) break;
              }
            }
          }
          // Once the sim has moved far enough from the obstacle, re-enable collisions
          if (touchingObstacle && simDist > BALL_R * 2) {
            touchingObstacle = false;
          }

          // Wall collisions (circle vs segment — full BALL_R, same as physics)
          if (polys && bounces <= MAX_BOUNCES) {
            for (let pi = 0; pi < polys.length; pi++) {
              const poly = polys[pi];
              const pn = poly.length;
              for (let ei = 0; ei < pn; ei++) {
                if (pi === 0 && openEdgesArr.includes(ei)) continue;
                const ej = (ei + 1) % pn;
                const hit = circleSegment(sx, sy, simRwall, poly[ei].x, poly[ei].y, poly[ej].x, poly[ej].y);
                if (hit) {
                  sx += hit.nx * hit.overlap;
                  sy += hit.ny * hit.overlap;
                  const dot2 = svx * hit.nx + svy * hit.ny;
                  svx = svx - 2 * dot2 * hit.nx;
                  svy = svy - 2 * dot2 * hit.ny;
                  bounces++;
                  if (bounces > MAX_BOUNCES) break;
                }
              }
              if (bounces > MAX_BOUNCES) break;
            }
          }

          // Record dot at progressive spacing thresholds
          if (dotIdx < totalDots && simDist >= dotThresholds[dotIdx]) {
            pathDots.push({ x: sx, y: sy });
            dotIdx++;
          }
        }

        // Draw simulated path dots
        ctx.fillStyle = dotColor;
        for (let i = 0; i < pathDots.length; i++) {
          const fade = 1 - ((i + 1) / totalDots) * 0.6;
          ctx.globalAlpha = fade;
          ctx.beginPath();
          ctx.arc(pathDots[i].x, pathDots[i].y, dotR, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.globalAlpha = 1;

        // Power ring around ball
        ctx.beginPath();
        ctx.arc(b.x, b.y, BALL_R + 5, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * power);
        ctx.strokeStyle = power > 0.8 ? '#EF4444' : power > 0.5 ? '#F59E0B' : '#22C55E';
        ctx.lineWidth = 3;
        ctx.lineCap = 'round';
        ctx.stroke();
        // Outer contrast ring so power arc is visible on any background
        ctx.beginPath();
        ctx.arc(b.x, b.y, BALL_R + 5, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * power);
        ctx.strokeStyle = aimOnLight ? 'rgba(0,0,0,0.15)' : 'rgba(255,255,255,0.15)';
        ctx.lineWidth = 5;
        ctx.stroke();
        // Redraw colored arc on top so it stays crisp
        ctx.beginPath();
        ctx.arc(b.x, b.y, BALL_R + 5, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * power);
        ctx.strokeStyle = power > 0.8 ? '#EF4444' : power > 0.5 ? '#F59E0B' : '#22C55E';
        ctx.lineWidth = 3;
        ctx.stroke();
        ctx.lineCap = 'butt';

        ctx.restore();
      }
    }

    // ── Per-hole elapsed timer (HTML overlay — updated via ref) ───
    if (timerElRef.current) {
      if (!ballSunk.current && !fadeAnim.current.active && holeTimerRunning.current) {
        timerElRef.current.style.opacity = '1';
        const elapsed = Math.floor(holeElapsedRef.current);
        const mins = Math.floor(elapsed / 60);
        const secs = elapsed % 60;
        timerElRef.current.querySelector('span').textContent =
          mins > 0 ? `${mins}:${String(secs).padStart(2, '0')}` : `${secs}s`;
      } else {
        timerElRef.current.style.opacity = '0';
      }
    }

    // ── Hole info HUD (HTML overlay — updated via ref) ───
    if (holeInfoRef.current) {
      if (!fadeAnim.current.active) {
        holeInfoRef.current.style.opacity = '1';
        holeInfoRef.current.querySelector('[data-hole]').textContent =
          `Hole ${holeIdx.current + 1}/${holesRef.current.length}`;
        holeInfoRef.current.querySelector('[data-strokes]').textContent =
          `Stroke ${strokes.current} · Par ${h.par}`;
        holeInfoRef.current.querySelector('[data-score]').textContent =
          totalScore.current;
      } else {
        holeInfoRef.current.style.opacity = '0';
      }
    }

    // ── Particles — sparkle burst ───────
    if (particlesRef.current.length > 0) {
      for (const p of particlesRef.current) {
        ctx.globalAlpha = p.opacity;
        ctx.fillStyle = p.color;
        const sz = p.size * Math.max(0.3, p.opacity);
        if (p.shape === 'star') {
          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(p.rotation || 0);
          ctx.beginPath();
          for (let si = 0; si < 4; si++) {
            const a = (si / 4) * Math.PI * 2;
            ctx.moveTo(0, 0);
            ctx.lineTo(Math.cos(a) * sz * 1.5, Math.sin(a) * sz * 1.5);
          }
          ctx.lineWidth = 1.2;
          ctx.strokeStyle = p.color;
          ctx.stroke();
          ctx.restore();
        } else {
          ctx.beginPath();
          ctx.arc(p.x, p.y, sz, 0, Math.PI * 2);
          ctx.fill();
        }
      }
      ctx.globalAlpha = 1;
    }

    // ── Floating score texts (compact, flappy-style) ──────
    if (floatingTextsRef.current.length > 0) {
      const c = col.current;
      ctx.font = 'bold 18px system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.strokeStyle = 'rgba(0,0,0,0.5)';
      ctx.lineWidth = 3;
      ctx.lineJoin = 'round';
      ctx.fillStyle = c.ball;
      for (const ft of floatingTextsRef.current) {
        ft.y = Math.max(30, Math.min(VH - 30, ft.y));
        if (ft.y <= 35 && ft.velocity < 0) ft.velocity = Math.abs(ft.velocity) * 0.3;
        ctx.globalAlpha = ft.life;
        ctx.strokeText(ft.text, ft.x, ft.y);
        ctx.fillText(ft.text, ft.x, ft.y);
      }
      ctx.globalAlpha = 1;
    }

    ctx.restore();

    // ── Foreground cloud wisps (parallax depth) ──
    if (!bgImg && cloudsRef.current) {
      ctx.save();
      ctx.scale(dpr * s, dpr * s);
      const time2 = frameTimeRef.current;
      for (const cloud of cloudsRef.current) {
        if (cloud.layer !== 'front') continue;
        ctx.globalAlpha = cloud.opacity * 0.2; // Very subtle
        ctx.fillStyle = '#fff';
        const drift = (time2 * cloud.speed * 15) % (vw + 200);
        const cx2 = ((cloud.x + drift + vw * 0.2) % (vw + 200)) - 100;
        ctx.beginPath();
        for (const blob of cloud.blobs) {
          ctx.moveTo(cx2 + blob.ox + blob.r * 1.3, cloud.y + blob.oy);
          ctx.arc(cx2 + blob.ox, cloud.y + blob.oy, blob.r * 1.3, 0, Math.PI * 2);
        }
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      ctx.restore();
    }

    // ── Transition overlay — simple crossfade ──
    if (fadeAnim.current.active) {
      const t = fadeAnim.current.t;
      // Fade up to black at midpoint, then back down
      const oa = t < 0.5
        ? (t / 0.5) * 0.7             // 0 → 0.7
        : ((1 - t) / 0.5) * 0.7;      // 0.7 → 0
      if (oa > 0.001) {
        ctx.save();
        ctx.scale(dpr * s, dpr * s);
        ctx.fillStyle = `rgba(0,0,0,${oa})`;
        ctx.fillRect(0, 0, vw, vh);
        ctx.restore();
      }
    }

    // ── Hole-in-one flash overlay ──
    if (flashAnim.current.active) {
      const fa = Math.max(0, 1 - flashAnim.current.t) * 0.4;
      if (fa > 0.001) {
        ctx.save();
        ctx.scale(dpr * s, dpr * s);
        ctx.fillStyle = flashAnim.current.color === 'danger'
          ? `rgba(255,70,40,${fa})`
          : `rgba(255,255,200,${fa})`;
        ctx.fillRect(0, 0, vw, vh);
        ctx.restore();
      }
    }
  }, []);

  // ── Main game loop ─────────────────────────────────────
  const loop = useCallback((timestamp) => {
    if (done.current) return;
    try {

    // Compute delta time — normalise to 60fps baseline
    const rawDt = lastTimeRef.current ? (timestamp - lastTimeRef.current) / 1000 : 0;
    lastTimeRef.current = timestamp;
    const dt = Math.min(rawDt, 0.05); // clamp to 50ms to prevent spiral of death
    const dt60 = dt * 60; // 1.0 at 60fps, 0.5 at 120fps
    dt60Ref.current = dt60;

    // Countdown timer + elapsed tracker (pauses during animations, never forces hole end)
    if (holeTimerRunning.current
        && !sinkAnim.current.active && !fallAnim.current.active && !fadeAnim.current.active) {
      holeElapsedRef.current += dt;
      if (holeTimerRef.current > 0) {
        holeTimerRef.current -= dt;
        if (holeTimerRef.current < 0) holeTimerRef.current = 0;
      }
    }

    if (!sinkAnim.current.active && !fadeAnim.current.active && !fallAnim.current.active) {
      updatePhysics();
    }

    if (sinkAnim.current.active) {
      sinkAnim.current.t += 1.2 * dt; // ~0.83s total sink animation
      if (sinkAnim.current.t >= 1) {
        sinkAnim.current.active = false;
        ballSunk.current = true;
        // Go straight to next hole or finish
        const nextIdx = holeIdx.current + 1;
        if (nextIdx >= holesRef.current.length) {
          done.current = true;
          if (onComplete) onComplete(totalScore.current, { holeScores: holeScores.current });
          return;
        }
        fadeAnim.current = { active: true, t: 0, _nextIdx: nextIdx };
      }
    }

    // Fall-off-edge animation — real physics: momentum + gravity + perspective shrink
    if (fallAnim.current.active) {
      const fa = fallAnim.current;
      fa.t += 1.2 * dt; // ~0.83s total
      // Horizontal momentum (preserved from exit velocity, slight air drag)
      fa.x += (fa.vx || 0) * dt60;
      fa.y += (fa.vy || 0) * dt60;
      fa.vx *= 0.99;
      fa.vy *= 0.99;
      // Gravity — ball accelerates downward as it falls off the island
      // Simulated as increasing "fall depth" below the island surface
      if (!fa.fallZ) fa.fallZ = 0;
      if (!fa.fallVZ) fa.fallVZ = 0;
      fa.fallVZ += 0.35 * dt60; // gravity acceleration
      fa.fallZ += fa.fallVZ * dt60;
      if (fa.t >= 1) {
        fa.active = false;
        const b = ball.current;
        b.x = lastShotPos.current.x;
        b.y = lastShotPos.current.y;
        b.vx = 0; b.vy = 0;

        // Check max strokes after fall-off reset — prevent unlimited extra shots
        if (strokes.current >= MAX_STROKES) {
          holeTimerRunning.current = false;
          const pts = calcHolePoints(MAX_STROKES, holeElapsedRef.current);
          totalScore.current += pts;
          holeScores.current.push({ strokes: MAX_STROKES, points: pts });
          if (onScoreChange) onScoreChange(totalScore.current);
    
          if (audioPoolRef.current?._urls?.death) {
            playSound(audioPoolRef.current, 'death');
          } else { beep(120, 0.3, 0.12); }
          vibrate(30);
          const nextIdx = holeIdx.current + 1;
          if (nextIdx >= holesRef.current.length) {
            done.current = true;
            if (onComplete) onComplete(totalScore.current, { holeScores: holeScores.current });
            return;
          }
          fadeAnim.current = { active: true, t: 0, _nextIdx: nextIdx };
          if (floatingTextsRef.current.length >= 3) floatingTextsRef.current.shift();
          floatingTextsRef.current.push({
            x: b.x, y: b.y - 30,
            text: `+${pts}`, life: 1.0, velocity: -0.7, points: pts,
          });
        }
      }
    }

    if (fadeAnim.current.active) {
      fadeAnim.current.t += 0.9 * dt; // ~1.1s total crossfade
      // Switch hole at midpoint (peak darkness)
      if (fadeAnim.current.t >= 0.5 && fadeAnim.current._nextIdx !== undefined) {
        const nextIdx = fadeAnim.current._nextIdx;
        const currentT = fadeAnim.current.t;
        holeIdx.current = nextIdx;
        setupHole(nextIdx);
        fadeAnim.current = { active: true, t: currentT };
      }
      if (fadeAnim.current.t >= 1) {
        fadeAnim.current.active = false;
      }
    }

    // Update floating texts
    for (let i = floatingTextsRef.current.length - 1; i >= 0; i--) {
      const ft = floatingTextsRef.current[i];
      ft.y += ft.velocity * dt60;
      ft.life -= 0.55 * dt; // slower fade — stays visible ~1.8s
      if (ft.life <= 0) {
        floatingTextsRef.current.splice(i, 1);
      }
    }

    // Update particles — gravity + spin + fade
    for (let i = particlesRef.current.length - 1; i >= 0; i--) {
      const p = particlesRef.current[i];
      p.x += p.vx * dt60;
      p.y += p.vy * dt60;
      p.vy += 0.08 * dt60; // lighter gravity — float longer
      p.vx *= 0.995; // gentle air resistance
      if (p.rotSpeed) p.rotation += p.rotSpeed * dt;
      p.life += dt;
      p.opacity = Math.max(0, 1 - p.life * 1.5); // slower fade
      if (p.opacity <= 0 || p.life > 0.8) {
        particlesRef.current.splice(i, 1);
      }
    }

    // Flash animation (hole-in-one)
    if (flashAnim.current.active) {
      flashAnim.current.t += 3 * dt;
      if (flashAnim.current.t >= 1) flashAnim.current.active = false;
    }

    // Screen shake decay (exponential, matches stacker pattern)
    const sk = shakeRef.current;
    if (sk.amount > 0.01) {
      sk.amount *= Math.pow(0.82, dt * 60);
      sk.x = (Math.random() - 0.5) * 2 * sk.amount;
      sk.y = (Math.random() - 0.5) * 2 * sk.amount;
    } else { sk.amount = 0; sk.x = 0; sk.y = 0; }

    // Advance frame time for flag bob
    frameTimeRef.current += dt;

    draw();
    } catch (err) {
      console.error('[golf-loop] CRASH:', err);
      // Unwind stale save stack from crashed draw() to prevent
      // transforms/clips accumulating across frames
      const ctx = ctxRef.current;
      if (ctx) {
        for (let i = 0; i < 20; i++) ctx.restore();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.globalAlpha = 1;
      }
    }
    rafRef.current = requestAnimationFrame(loop);
  }, [updatePhysics, draw, onScoreChange, onComplete, setupHole, spawnParticles]);

  // ── Pointer handlers ───────────────────────────────────
  const getPointerPos = useCallback((e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return toVirtual(clientX - rect.left, clientY - rect.top);
  }, [toVirtual]);

  const getPointerEnd = useCallback((e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const clientX = e.changedTouches ? e.changedTouches[0].clientX : e.clientX;
    const clientY = e.changedTouches ? e.changedTouches[0].clientY : e.clientY;
    return toVirtual(clientX - rect.left, clientY - rect.top);
  }, [toVirtual]);

  const onPointerDown = useCallback((e) => {
    e.preventDefault();
    if (!gameActive.current) return;
    if (moving.current || done.current || sinkAnim.current.active || fallAnim.current.active || fadeAnim.current.active) return;
    const pos = getPointerPos(e);
    aiming.current = true;
    aimStart.current = { x: pos.x, y: pos.y };
    aimCur.current = { x: pos.x, y: pos.y };
  }, [getPointerPos]);

  const onPointerMove = useCallback((e) => {
    e.preventDefault();
    if (!aiming.current) return;
    const pos = getPointerPos(e);
    aimCur.current = { x: pos.x, y: pos.y };
  }, [getPointerPos]);

  const onPointerUp = useCallback((e) => {
    e.preventDefault();
    if (!aiming.current) return;
    aiming.current = false;
    const pos = getPointerEnd(e);
    const as = aimStart.current;
    const dx = pos.x - as.x;
    const dy = pos.y - as.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist > 10) {
      const power = Math.min(dist, MAX_DRAG) / MAX_DRAG;
      const ndx = -dx / dist;
      const ndy = -dy / dist;
      shoot(ndx, ndy, power);
    }
  }, [getPointerEnd, shoot]);

  // ── Mount / unmount ────────────────────────────────────
  useEffect(() => {
    const cvs = canvasRef.current;
    if (!cvs) return;
    ctxRef.current = cvs.getContext('2d');
    resize();
    setupHole(0);

    rafRef.current = requestAnimationFrame(loop);

    // Listen to window resize (covers both direct play and iframe preview)
    window.addEventListener('resize', resize);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', resize);
    };
  }, [resize, setupHole, loop]);

  // ── Attach input listeners ─────────────────────────────
  useEffect(() => {
    const cvs = canvasRef.current;
    if (!cvs) return;
    cvs.addEventListener('pointerdown', onPointerDown, { passive: false });
    cvs.addEventListener('pointermove', onPointerMove, { passive: false });
    cvs.addEventListener('pointerup', onPointerUp, { passive: false });
    cvs.addEventListener('pointercancel', onPointerUp, { passive: false });
    cvs.addEventListener('touchstart', onPointerDown, { passive: false });
    cvs.addEventListener('touchmove', onPointerMove, { passive: false });
    cvs.addEventListener('touchend', onPointerUp, { passive: false });
    return () => {
      cvs.removeEventListener('pointerdown', onPointerDown);
      cvs.removeEventListener('pointermove', onPointerMove);
      cvs.removeEventListener('pointerup', onPointerUp);
      cvs.removeEventListener('pointercancel', onPointerUp);
      cvs.removeEventListener('touchstart', onPointerDown);
      cvs.removeEventListener('touchmove', onPointerMove);
      cvs.removeEventListener('touchend', onPointerUp);
    };
  }, [onPointerDown, onPointerMove, onPointerUp]);

  return (
    <div
      ref={containerRef}
      style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', touchAction: 'none', overflow: 'hidden', zIndex: 1 }}
    >
      <canvas
        ref={canvasRef}
        style={{ display: 'block', width: '100%', height: '100%', cursor: 'crosshair' }}
      />
      {/* Hole info + score HUD — compact pill, top-left */}
      <div
        ref={holeInfoRef}
        style={{
          position: 'absolute', top: 10, left: 10, opacity: 0,
          display: 'flex', alignItems: 'center', gap: 8,
          background: 'rgba(0,0,0,0.3)', borderRadius: 12, padding: '4px 10px',
          pointerEvents: 'none', zIndex: 5, transition: 'opacity 0.2s',
        }}
      >
        <div style={{ lineHeight: 1.2 }}>
          <div data-hole="" style={{ color: 'rgba(255,255,255,0.85)', fontSize: 12, fontWeight: 700 }}>Hole 1/3</div>
          <div data-strokes="" style={{ color: 'rgba(255,255,255,0.50)', fontSize: 10, fontWeight: 500 }}>Stroke 0 · Par 2</div>
        </div>
        <div style={{ width: 1, height: 22, background: 'rgba(255,255,255,0.15)' }} />
        <div data-score="" style={{ color: 'rgba(255,255,255,0.85)', fontSize: 14, fontWeight: 700, fontVariantNumeric: 'tabular-nums', minWidth: 16, textAlign: 'center' }}>0</div>
      </div>
      {/* Per-hole elapsed timer — subtle count-up */}
      <div
        ref={timerElRef}
        style={{
          position: 'absolute', top: 10, right: 10, opacity: 0,
          background: 'rgba(0,0,0,0.3)', borderRadius: 12, padding: '3px 8px',
          pointerEvents: 'none', zIndex: 5, transition: 'opacity 0.2s',
        }}
      >
        <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>0s</span>
      </div>
      {/* Countdown Overlay — Shared Component */}
      <GameStartSequence
        isLoading={isLoading}
        countdown={countdown}
        isVisible={showOverlay}
        colors={colors}
        font={primaryFont}
        fontScale={fontScale}
        frameStyle={frameStyle}
        instructions="Pull back to aim • Release to putt"
      />
    </div>
  );
};

// ── Haptic helper ────────────────────────────────────────
function vibrate(ms) {
  try { navigator?.vibrate?.(ms); } catch (_) { /* not supported */ }
}

// ── Turf pattern helper ──────────────────────────────────
function drawTurf(ctx, baseColor, style, pa) {
  if (style === 'solid') return; // base fill already done
  // Use luminance to decide: lighten on dark floors, darken on light floors
  const [r, g, b] = hexToRgb(baseColor);
  const lum = (r * 0.299 + g * 0.587 + b * 0.114) / 255;
  const alt = lum < 0.35 ? lighten(baseColor, 0.12) : darken(baseColor, 0.08);
  const ts = 20; // tile size
  if (style === 'stripes') {
    const bandH = 16;
    ctx.fillStyle = alt;
    for (let ty = pa.y; ty < pa.y + pa.h; ty += bandH * 2) {
      ctx.fillRect(pa.x, ty, pa.w, bandH);
    }
  } else if (style === 'diamonds') {
    ctx.save();
    ctx.translate(pa.x + pa.w / 2, pa.y + pa.h / 2);
    ctx.rotate(Math.PI / 4);
    const ext = Math.max(pa.w, pa.h) * 1.5;
    for (let tx = -ext; tx < ext; tx += ts) {
      for (let ty = -ext; ty < ext; ty += ts) {
        const tc = Math.floor((tx + ext) / ts);
        const tr = Math.floor((ty + ext) / ts);
        if ((tc + tr) % 2 !== 0) {
          ctx.fillStyle = alt;
          ctx.fillRect(tx, ty, ts, ts);
        }
      }
    }
    ctx.restore();
  } else {
    // checkerboard (default)
    for (let tx = pa.x; tx < pa.x + pa.w; tx += ts) {
      for (let ty = pa.y; ty < pa.y + pa.h; ty += ts) {
        const tc = Math.floor((tx - pa.x) / ts);
        const tr = Math.floor((ty - pa.y) / ts);
        if ((tc + tr) % 2 !== 0) {
          ctx.fillStyle = alt;
          ctx.fillRect(tx, ty, ts, ts);
        }
      }
    }
  }
}

// ── Color helpers ────────────────────────────────────────
function darken(hex, amt) {
  const [r, g, b] = hexToRgb(hex);
  return rgbToHex(Math.round(r * (1 - amt)), Math.round(g * (1 - amt)), Math.round(b * (1 - amt)));
}
function lighten(hex, amt) {
  const [r, g, b] = hexToRgb(hex);
  return rgbToHex(
    Math.round(r + (255 - r) * amt),
    Math.round(g + (255 - g) * amt),
    Math.round(b + (255 - b) * amt),
  );
}
function hexToRgb(hex) {
  const h = hex.replace('#', '');
  return [parseInt(h.substring(0, 2), 16), parseInt(h.substring(2, 4), 16), parseInt(h.substring(4, 6), 16)];
}
function rgbToHex(r, g, b) {
  return '#' + [r, g, b].map(v => Math.max(0, Math.min(255, v)).toString(16).padStart(2, '0')).join('');
}
function isLightColor(hex) {
  const [r, g, b] = hexToRgb(hex);
  return (r * 299 + g * 587 + b * 114) / 1000 > 150;
}
function coursePath(ctx, shape) {
  if (!shape || shape.length === 0) return;
  // Support compound shapes (array of polygons) for holes/cutouts
  const polys = Array.isArray(shape[0]) ? shape : [shape];
  for (const poly of polys) {
    if (!poly || poly.length < 3) continue;
    ctx.moveTo(poly[0].x, poly[0].y);
    for (let i = 1; i < poly.length; i++) {
      ctx.lineTo(poly[i].x, poly[i].y);
    }
    ctx.closePath();
  }
}
// Stroke path that skips open edges — groups consecutive closed edges
// into connected polyline runs so corners get proper miter joins.
function courseStrokePath(ctx, shape, openEdges) {
  if (!shape || shape.length === 0) return;
  if (!openEdges || openEdges.length === 0) { coursePath(ctx, shape); return; }
  const polys = Array.isArray(shape[0]) ? shape : [shape];
  for (let pi = 0; pi < polys.length; pi++) {
    const poly = polys[pi];
    if (!poly || poly.length < 3) continue;
    const n = poly.length;
    const isOuter = pi === 0; // open edges only apply to the outer polygon
    // Inner polygons (cutouts) have no open edges — draw as closed path
    // so all corners get proper miter joins (closePath joins last→first).
    if (!isOuter) {
      ctx.moveTo(poly[0].x, poly[0].y);
      for (let i = 1; i < n; i++) ctx.lineTo(poly[i].x, poly[i].y);
      ctx.closePath();
      continue;
    }
    // Outer polygon: start iteration from an open edge so we don't split a
    // closed run that wraps around vertex 0.
    let start = 0;
    for (let i = 0; i < n; i++) {
      if (openEdges.includes(i)) { start = (i + 1) % n; break; }
    }
    let inRun = false;
    for (let step = 0; step < n; step++) {
      const i = (start + step) % n;
      if (!openEdges.includes(i)) {
        if (!inRun) {
          ctx.moveTo(poly[i].x, poly[i].y);
          inRun = true;
        }
        const j = (i + 1) % n;
        ctx.lineTo(poly[j].x, poly[j].y);
      } else {
        inRun = false;
      }
    }
  }
}
function roundRect(ctx, x, y, w, h, r) {
  r = Math.min(r, w / 2, h / 2);
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
}

export default Game;
